import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import costService from '../services/costService';
import categoriesService from '../services/categoriesService';
import suppliersService from '../services/suppliersService';
import unitsService from '../services/unitsService';
import LoadingSpinner from './LoadingSpinner';
import CostVariationsManager from './CostVariationsManager';
import DynamicPricingManager from './DynamicPricingManager';
import MaterialPricingWeightsManager from './MaterialPricingWeightsManager';
import CortesPdfUpload from './CortesPdfUpload';
import CortesPdfList from './CortesPdfList';
import PurchaseItemsGrid from './PurchaseItemsGrid';
import toast from 'react-hot-toast';
import {
  Check, X, Users, Search, Trash2,
  Building2, DollarSign, CheckCircle2, Clock, ArrowDownRight,
  Layers, Package, Lock, Unlock, Plus, AlertCircle, FileText, FolderTree, Settings, Save, Edit2, Calendar,
  ArrowUpDown, ArrowUp, ArrowDown, Upload, Download
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from './ConfirmModal';
import api from '../services/api';
import { useSearchParams } from 'react-router-dom';

export const formatLocalDateBR = (dateStr) => {
  if (!dateStr) return '-';
  if (typeof dateStr === 'string') {
    const clean = dateStr.split('T')[0].split(' ')[0];
    const parts = clean.split('-');
    if (parts.length === 3) {
      return `${parts[2].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[0]}`;
    }
  }
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pt-BR');
};

export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  if (typeof dateStr === 'string') {
    const clean = dateStr.split('T')[0].split(' ')[0];
    const parts = clean.split('-');
    if (parts.length === 3) {
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    }
  }
  return new Date(dateStr);
};

export const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  if (typeof dateStr === 'string') {
    const clean = dateStr.split('T')[0].split(' ')[0];
    const parts = clean.split('-');
    if (parts.length === 3) {
      return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
    }
  }
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

const CostsManagementEnhanced = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [costs, setCosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [, setUnits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showNewExpenseMenu, setShowNewExpenseMenu] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [showVariations, setShowVariations] = useState(false);
  const [selectedCostForVariations, setSelectedCostForVariations] = useState(null);
  const [showDynamicPricing, setShowDynamicPricing] = useState(false);
  const [selectedCostForPricing, setSelectedCostForPricing] = useState(null);
  const [showMaterialWeights, setShowMaterialWeights] = useState(false);
  const [showCortesPdfUpload, setShowCortesPdfUpload] = useState(false);
  const [showCortesPdfList, setShowCortesPdfList] = useState(false);
  const [activeTab, setActiveTab] = useState('general'); // 'general', 'team', 'company', 'tax', 'custom'
  const [teamExpenseMode, setTeamExpenseMode] = useState('total'); // 'total' or 'unit'
  const [customExpensesData, setCustomExpensesData] = useState([]);
  const [customExpensesLoading, setCustomExpensesLoading] = useState(false);
  const [customExpenseStatusFilter, setCustomExpenseStatusFilter] = useState('ALL'); // 'ALL' | 'PAID' | 'PARTIAL' | 'PENDING'
  const [customExpenseSearchTerm, setCustomExpenseSearchTerm] = useState('');
  const [confirmDeleteCustomCost, setConfirmDeleteCustomCost] = useState({ show: false, cost: null });
  const isManager = ['GERENCIA', 'ADMIN', 'CEO'].includes(user?.role) || user?.permissions?.includes('all:full');
  const [selectedCostAbates, setSelectedCostAbates] = useState(null);
  const [showAbatesModal, setShowAbatesModal] = useState(false);
  const [costAbatesList, setCostAbatesList] = useState([]);
  const [costAbatesLoading, setCostAbatesLoading] = useState(false);
  const [showCustomExpenseForm, setShowCustomExpenseForm] = useState(false);
  const [customExpenseMode, setCustomExpenseMode] = useState('simple'); // 'simple' | 'detailed'
  const [customExpenseFormData, setCustomExpenseFormData] = useState({
    supplier_id: '',
    description: '',
    base_price_per_unit: '',
    simple_amount: '',
    due_date: '',
    order_period_start: new Date().toISOString().split('T')[0],
    order_period_end: '',
    fiscal_number: '',
    fiscal_series: '',
    fiscal_key: '',
    emission_date: '',
    entry_date: ''
  });
  const [customExpenseFiles, setCustomExpenseFiles] = useState([]);
  const [customExpenseFiscalErrors, setCustomExpenseFiscalErrors] = useState({});
  const [customExpenseItems, setCustomExpenseItems] = useState([]);
  const [customExpenseValidation, setCustomExpenseValidation] = useState({
    isValid: false,
    itemCount: 0,
    totalAmount: 0
  });

  // Estados para Subpedidos e Incrementos
  const [showSubOrdersModal, setShowSubOrdersModal] = useState(false);
  const [selectedCostForSubOrders, setSelectedCostForSubOrders] = useState(null);
  const [subOrdersData, setSubOrdersData] = useState({ cost: null, sub_orders: [], unassigned_items: [] });
  const [loadingSubOrders, setLoadingSubOrders] = useState(false);

  const [showNewSubOrderModal, setShowNewSubOrderModal] = useState(false);
  const [newSubOrderFormData, setNewSubOrderFormData] = useState({
    sub_order_number: '',
    description: '',
    sub_order_date: new Date().toISOString().split('T')[0]
  });
  const [newSubOrderItems, setNewSubOrderItems] = useState([]);
  const [newSubOrderValidation, setNewSubOrderValidation] = useState({
    isValid: false,
    itemCount: 0,
    totalAmount: 0
  });

  const [showConfirmStockModal, setShowConfirmStockModal] = useState(false);
  const [confirmStockData, setConfirmStockData] = useState({
    costId: null,
    subOrderId: null,
    subOrderNumber: '',
    warehouse_id: '',
    observations: ''
  });
  const [confirmingStockLoading, setConfirmingStockLoading] = useState(false);
  const [showPayByCompanyModal, setShowPayByCompanyModal] = useState(false);
  const [payByCompanyAmount, setPayByCompanyAmount] = useState('');
  const [selectedCosts, setSelectedCosts] = useState([]); // Para delete em massa
  const [showParcels, setShowParcels] = useState(false); // Mostrar/ocultar seção de parcelas
  const [parcelsList, setParcelsList] = useState([]); // Lista de parcelas da despesa recorrente
  const [expandedCosts, setExpandedCosts] = useState({}); // Controle de expansão de linhas
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [confirmParcel, setConfirmParcel] = useState({ show: false, parcel: null, payment_method: 'pix' });
  const [confirmDelete, setConfirmDelete] = useState({ show: false, costId: null });
  const [confirmBulkDelete, setConfirmBulkDelete] = useState({ show: false });
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const filteredCosts = useMemo(() => {
    let result = costs;
    if (activeTab === 'general') {
      result = costs;
    } else if (activeTab === 'company') {
      result = costs.filter(c => c.expense_category === 'company' || (!c.expense_category && c.cost_type === 'operational_expense'));
    } else if (activeTab === 'team') {
      result = costs.filter(c => c.expense_category === 'team');
    } else if (activeTab === 'tax') {
      result = costs.filter(c =>
        c.expense_category === 'tax' ||
        (c.expense_category === 'company' && (
          (c.subcategory || '').toLowerCase().includes('tribut') ||
          (c.subcategory || '').toLowerCase().includes('impost') ||
          (c.category_name || '').toLowerCase().includes('tribut') ||
          (c.category_name || '').toLowerCase().includes('impost') ||
          (c.category_name || '').toLowerCase().includes('simples') ||
          (c.category_name || '').toLowerCase().includes('fgts') ||
          (c.category_name || '').toLowerCase().includes('receita')
        ))
      );
    } else if (activeTab === 'custom') {
      result = costs.filter(c => c.cost_type === 'CUSTOM');
    }

    if (!sortConfig.key) return result;

    return [...result].sort((a, b) => {
      if (sortConfig.key === 'date') {
        const getDateVal = (item) => {
          const d = item.expense_date || item.created_at;
          if (!d) return 0;
          if (typeof d === 'string') {
            const clean = d.split('T')[0].split(' ')[0];
            const parts = clean.split('-');
            if (parts.length === 3) {
              return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10)).getTime();
            }
          }
          const t = new Date(d).getTime();
          return isNaN(t) ? 0 : t;
        };
        const aVal = getDateVal(a);
        const bVal = getDateVal(b);
        const diff = aVal - bVal;
        if (diff !== 0) return sortConfig.direction === 'asc' ? diff : -diff;
        return (b.id || 0) - (a.id || 0);
      }

      if (sortConfig.key === 'category') {
        const aCat = (a.category_name || '').trim();
        const bCat = (b.category_name || '').trim();
        const diff = aCat.localeCompare(bCat, 'pt-BR', { sensitivity: 'base' });
        return sortConfig.direction === 'asc' ? diff : -diff;
      }

      if (sortConfig.key === 'description') {
        const aDesc = (a.description || '').trim();
        const bDesc = (b.description || '').trim();
        const diff = aDesc.localeCompare(bDesc, 'pt-BR', { sensitivity: 'base' });
        return sortConfig.direction === 'asc' ? diff : -diff;
      }

      if (sortConfig.key === 'cost') {
        const aVal = parseFloat(a.cost_type === 'operational_expense' ? a.base_price_per_unit : a.unit_cost) || 0;
        const bVal = parseFloat(b.cost_type === 'operational_expense' ? b.base_price_per_unit : b.unit_cost) || 0;
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      if (sortConfig.key === 'sale') {
        const getSaleVal = (item) => {
          if (item.cost_type === 'CUSTOM') return -2;
          if (item.cost_type === 'operational_expense') return -1;
          return parseFloat(item.suggested_selling_price) || 0;
        };
        const aVal = getSaleVal(a);
        const bVal = getSaleVal(b);
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      return 0;
    });
  }, [costs, activeTab, sortConfig]);

  const handleSort = useCallback((key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      const defaultDesc = ['date', 'cost', 'sale'].includes(key);
      return {
        key,
        direction: defaultDesc ? 'desc' : 'asc'
      };
    });
    setCurrentPage(1);
  }, []);

  // Se vier com ?costId=123&openParcels=true da notificação, abre o modal de parcelas diretamente!
  useEffect(() => {
    const costIdParam = searchParams.get('costId');
    const openParcelsParam = searchParams.get('openParcels');
    if (costIdParam && openParcelsParam === 'true') {
      costService.getCostById(costIdParam).then(res => {
        if (res?.data) {
          setEditingCost(res.data);
          setShowParcels(true);
          fetchParcels(res.data.id);
        }
      }).catch(err => {
        console.error('Erro ao abrir parcelas da despesa recorrente via notificação:', err);
      });
    }
  }, [searchParams]);

  // Função auxiliar para determinar o status de pagamento/abate da despesa personalizada
  const getCustomExpensePaymentStatus = useCallback((cost) => {
    const totalAbated = parseFloat(cost.total_abated || 0);
    const baseTotal = parseFloat(cost.base_price_per_unit || 0);
    const remaining = parseFloat(cost.remaining_amount !== undefined ? cost.remaining_amount : Math.max(0, baseTotal - totalAbated));

    if (cost.abatement_status === 'PENDING') return 'PENDING';
    if (cost.abatement_status === 'PARTIAL') return 'PARTIAL';
    if (cost.abatement_status === 'PAID' || cost.abatement_status === 'TOTAL' || cost.abatement_status === 'COMPLETED') return 'PAID';

    if (totalAbated <= 0.01) return 'PENDING';
    if (remaining <= 0.01 && totalAbated > 0) return 'PAID';
    return 'PARTIAL';
  }, []);

  // Filtros e contadores para Despesas Personalizadas com Abates
  const customExpensesStats = useMemo(() => {
    if (!Array.isArray(customExpensesData)) return {
      total: 0, paid: 0, partial: 0, pending: 0,
      totalAmount: 0, totalAbated: 0, totalRemaining: 0
    };
    return customExpensesData.reduce((acc, cost) => {
      acc.total++;
      const st = getCustomExpensePaymentStatus(cost);
      if (st === 'PAID') acc.paid++;
      else if (st === 'PARTIAL') acc.partial++;
      else acc.pending++;

      const baseVal = parseFloat(cost.base_price_per_unit || 0);
      const abatedVal = parseFloat(cost.total_abated || 0);
      const remainingVal = parseFloat(cost.remaining_amount !== undefined ? cost.remaining_amount : Math.max(0, baseVal - abatedVal));

      acc.totalAmount += baseVal;
      acc.totalAbated += abatedVal;
      acc.totalRemaining += remainingVal;

      return acc;
    }, {
      total: 0, paid: 0, partial: 0, pending: 0,
      totalAmount: 0, totalAbated: 0, totalRemaining: 0
    });
  }, [customExpensesData, getCustomExpensePaymentStatus]);

  const filteredCustomExpenses = useMemo(() => {
    if (!Array.isArray(customExpensesData)) return [];
    return customExpensesData.filter((cost) => {
      // 1. Filtro por status (Já pagas, Parcialmente pagas, Não pagas)
      if (customExpenseStatusFilter !== 'ALL') {
        const st = getCustomExpensePaymentStatus(cost);
        if (customExpenseStatusFilter !== st) return false;
      }

      // 2. Filtro por busca de texto (Fornecedor, Descrição ou ID)
      if (customExpenseSearchTerm.trim()) {
        const term = customExpenseSearchTerm.toLowerCase();
        const matches =
          (cost.supplier_name && cost.supplier_name.toLowerCase().includes(term)) ||
          (cost.description && cost.description.toLowerCase().includes(term)) ||
          String(cost.id).includes(term);
        if (!matches) return false;
      }

      return true;
    });
  }, [customExpensesData, customExpenseStatusFilter, customExpenseSearchTerm, getCustomExpensePaymentStatus]);


  const [filters, setFilters] = useState({
    category_id: '',
    search: '',
    supplier_id: '',
    color: '',
    thickness: '',
    brand: '',
    start_date: '',
    end_date: ''
  });
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const loadCosts = useCallback(async (customFilters = filtersRef.current) => {
    try {
      const response = await costService.getAllCosts(customFilters);
      setCosts(response.data || []);
    } catch (error) {
      setError('Erro ao carregar custos');
      console.error(error);
    }
  }, []);

  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    setCurrentPage(1);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      loadCosts(filters);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [filters, activeTab, loadCosts]);

  const handleSearchChange = (event) => {
    setFilters(prev => ({ ...prev, search: event.target.value }));
  };

  // Formulário
  const [formData, setFormData] = useState({
    cost_type: 'product_cost', // NOVO: tipo de custo
    expense_category: '', // 'team', 'company' ou 'tax'
    category_id: '',
    subcategory: '',
    supplier_id: '',
    alternative_supplier_id: '',
    unit_id: '',
    description: '',
    base_price_per_unit: '',
    pricing_type: 'dynamic',
    unit_calculation_type: 'piece',
    default_model: '',
    min_order_quantity: 1,
    lead_time_days: 0,
    observations: '',
    custom_data: {},
    // Campos para despesas operacionais
    expense_date: '',
    responsible_person: '',
    selected_employees: [], // Array para múltiplos funcionários
    unit_value: '', // Valor por funcionário (para alimentação)
    vehicle: '',
    km_odometer: '',
    receipt_number: '',
    payment_method: '',
    is_reimbursable: false,
    reimbursement_status: 'not_applicable',
    // Campos de recorrência
    recurrence_type: 'none',
    recurrence_day: '',
    recurrence_end_date: '',
    next_due_date: '',
    is_auto_sync: false
  });

  // Effect para ajustar formulário quando muda a aba
  useEffect(() => {
    if (activeTab === 'company') {
      setFormData(prev => ({
        ...prev,
        cost_type: 'operational_expense',
        expense_category: 'company',
        responsible_person: 'EMPRESA', // Valor padrão para empresa
        selected_employees: [],
        base_price_per_unit: '',
        description: '',
        // Tentar pré-selecionar categoria Aluguel ou similar se existir
        category_id: categories.find(c => c.name.toLowerCase().includes('aluguel') || c.name.toLowerCase().includes('infraestrutura'))?.id || prev.category_id
      }));
    } else if (activeTab === 'team') {
      setFormData(prev => ({
        ...prev,
        cost_type: 'operational_expense',
        expense_category: 'team',
        responsible_person: '',
        base_price_per_unit: '',
        description: ''
      }));
    } else if (activeTab === 'tax') {
      setFormData(prev => ({
        ...prev,
        cost_type: 'operational_expense',
        expense_category: 'tax',
        responsible_person: 'EMPRESA',
        selected_employees: [],
        base_price_per_unit: '',
        description: '',
        // Pré-selecionar categoria de impostos se existir
        category_id: categories.find(c =>
          ['inss', 'fgts', 'darf', 'gps', 'gfip', 'iss', 'icms', 'pis', 'cofins', 'irpj', 'csll', 'taxas'].some(t =>
            c.name.toLowerCase().includes(t)
          )
        )?.id || prev.category_id
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        cost_type: 'product_cost',
        expense_category: '',
        responsible_person: '',
        selected_employees: [],
        base_price_per_unit: ''
      }));
    }
  }, [activeTab, categories]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'custom') {
      loadCustomExpenses();
    }
  }, [activeTab]);

  const loadCustomExpenses = async () => {
    try {
      setCustomExpensesLoading(true);
      const response = await costService.getCostsWithAbates();
      setCustomExpensesData(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar despesas com abates:', error);
      toast.error('Erro ao carregar despesas com abates');
    } finally {
      setCustomExpensesLoading(false);
    }
  };

  const loadCostAbates = async (costId) => {
    try {
      setCostAbatesLoading(true);
      const response = await costService.getCostAbates(costId);
      setCostAbatesList(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar abates:', error);
      toast.error('Erro ao carregar abates');
    } finally {
      setCostAbatesLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCosts(),
        loadCategories(),
        loadSuppliers(),
        loadUnits(),
        loadEmployees(),
        loadWarehouses()
      ]);
    } catch (error) {
      setError('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await api.get('/inventory/warehouses');
      setWarehouses(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Erro ao carregar armazéns:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesService.getAllCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const response = await suppliersService.getAllSuppliers();
      setSuppliers(response.data);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
    }
  };

  const loadUnits = async () => {
    try {
      const response = await unitsService.getAllUnits();
      setUnits(response.data);
    } catch (error) {
      console.error('Erro ao carregar unidades:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await costService.getActiveEmployees();
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      // Se for categoria de alimentação e houver múltiplos funcionários, calcular valor total
      if (name === 'unit_value' || name === 'selected_employees') {
        const selectedCategory = categories.find(c => String(c.id) === String(newData.category_id));
        const isAlimentacao = selectedCategory && selectedCategory.name?.toLowerCase().includes('alimentação');

        if (isAlimentacao && newData.selected_employees && newData.selected_employees.length > 0 && newData.unit_value) {
          const unitValue = parseFloat(newData.unit_value) || 0;
          const totalValue = unitValue * newData.selected_employees.length;
          newData.base_price_per_unit = totalValue.toFixed(2);
        }
      }

      return newData;
    });
  };

  const handleEmployeeSelection = (employeeId) => {
    setFormData(prev => {
      const selectedCategory = categories.find(c => String(c.id) === String(prev.category_id));
      const isAlimentacao = selectedCategory && selectedCategory.name?.toLowerCase().includes('alimentação');

      let newSelectedEmployees = [...(prev.selected_employees || [])];

      if (newSelectedEmployees.includes(employeeId)) {
        newSelectedEmployees = newSelectedEmployees.filter(id => id !== employeeId);
      } else {
        newSelectedEmployees.push(employeeId);
      }

      const newData = {
        ...prev,
        selected_employees: newSelectedEmployees,
        responsible_person: newSelectedEmployees.length === 1
          ? employees.find(e => e.id === employeeId)?.full_name || ''
          : newSelectedEmployees.length > 1
            ? `${newSelectedEmployees.length} funcionários selecionados`
            : ''
      };

      // Calcular valor total se for alimentação
      if (isAlimentacao && newSelectedEmployees.length > 0 && prev.unit_value) {
        const unitValue = parseFloat(prev.unit_value) || 0;
        const totalValue = unitValue * newSelectedEmployees.length;
        newData.base_price_per_unit = totalValue.toFixed(2);
      }

      return newData;
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const selectedCategory = categories.find(c => String(c.id) === String(formData.category_id));
      const isAlimentacao = selectedCategory && selectedCategory.name?.toLowerCase().includes('alimentação');
      const hasMultipleEmployees = isAlimentacao && formData.selected_employees && formData.selected_employees.length > 1;

      if (hasMultipleEmployees) {
        // Criar múltiplas despesas (uma para cada funcionário)
        const unitValue = parseFloat(formData.unit_value || 0);

        for (const employeeId of formData.selected_employees) {
          const employee = employees.find(e => e.id === employeeId);
          if (!employee) continue;

          const costData = {
            ...formData,
            cost_type: 'operational_expense',
            expense_category: 'team',
            base_price_per_unit: unitValue, // Valor unitário por funcionário
            responsible_person: user?.full_name || 'EMPRESA',
            employee_ids: [employeeId], // Enviar array com um único ID
            selected_employees: undefined, // Não enviar o array
            unit_value: undefined, // Não enviar o valor unitário
            supplier_id: formData.supplier_id && formData.supplier_id !== '' ? parseInt(formData.supplier_id) : null,
            alternative_supplier_id: formData.alternative_supplier_id && formData.alternative_supplier_id !== '' ? parseInt(formData.alternative_supplier_id) : null,
            unit_id: formData.unit_id && formData.unit_id !== '' ? parseInt(formData.unit_id) : null,
            category_id: formData.category_id && formData.category_id !== '' ? parseInt(formData.category_id) : null,
            thickness: formData.thickness ? parseFloat(formData.thickness) : null,
            size_width: formData.size_width ? parseFloat(formData.size_width) : null,
            size_height: formData.size_height ? parseFloat(formData.size_height) : null,
            size_length: formData.size_length ? parseFloat(formData.size_length) : null,
            min_order_quantity: parseInt(formData.min_order_quantity || 1),
            lead_time_days: parseInt(formData.lead_time_days || 0),
            km_odometer: formData.km_odometer && formData.km_odometer !== '' ? parseInt(formData.km_odometer) : null,
            description: formData.description // Manter descrição original
          };

          await costService.createCost(costData);
        }
      } else {
        // Criar uma única despesa
        const costData = {
          ...formData,
          cost_type: formData.cost_type || 'product_cost',
          expense_category: activeTab === 'team' ? 'team' : 'company',
          base_price_per_unit: parseFloat(formData.base_price_per_unit || formData.unit_cost || 0),
          employee_ids: (formData.selected_employees && formData.selected_employees.length > 0) ? formData.selected_employees : undefined,
          responsible_person: user?.full_name || 'EMPRESA',
          selected_employees: undefined,
          unit_value: undefined,
          supplier_id: formData.supplier_id && formData.supplier_id !== '' ? parseInt(formData.supplier_id) : null,
          alternative_supplier_id: formData.alternative_supplier_id && formData.alternative_supplier_id !== '' ? parseInt(formData.alternative_supplier_id) : null,
          unit_id: formData.unit_id && formData.unit_id !== '' ? parseInt(formData.unit_id) : null,
          category_id: formData.category_id && formData.category_id !== '' ? parseInt(formData.category_id) : null,
          thickness: formData.thickness ? parseFloat(formData.thickness) : null,
          size_width: formData.size_width ? parseFloat(formData.size_width) : null,
          size_height: formData.size_height ? parseFloat(formData.size_height) : null,
          size_length: formData.size_length ? parseFloat(formData.size_length) : null,
          min_order_quantity: parseInt(formData.min_order_quantity || 1),
          lead_time_days: parseInt(formData.lead_time_days || 0),
          km_odometer: formData.km_odometer && formData.km_odometer !== '' ? parseInt(formData.km_odometer) : null
        };

        if (editingCost) {
          await costService.updateCost(editingCost.id, costData);
        } else {
          await costService.createCost(costData);
        }
      }

      setShowForm(false);
      setEditingCost(null);
      resetForm();
      loadCosts();
      // Invalidar queries relacionadas ao financeiro
      queryClient.invalidateQueries(['finance-stats']);
      queryClient.invalidateQueries(['finance-expenses']);
      queryClient.invalidateQueries(['finance-forecast']);
      queryClient.invalidateQueries(['finance-forecasted-expenses']);
    } catch (error) {
      setError('Erro ao salvar custo');
      console.error(error);
    }
  };

  // Handler para criar despesa personalizada
  const handleCustomExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!customExpenseFormData.supplier_id) {
        toast.error('Selecione um fornecedor');
        return;
      }
      if (!customExpenseFormData.description) {
        toast.error('Informe a descrição');
        return;
      }

      let totalAmount = 0;
      let itemsPayload = [];

      if (customExpenseMode === 'detailed') {
        if (customExpenseItems.length === 0) {
          toast.error('Adicione pelo menos um item à despesa detalhada');
          return;
        }
        if (customExpenseValidation.totalAmount <= 0) {
          toast.error('O valor total deve ser maior que zero');
          return;
        }
        totalAmount = customExpenseValidation.totalAmount;
        itemsPayload = customExpenseItems.map(item => ({
          variant_id: item.variant_id,
          product_id: item.product_id,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price)
        }));
      } else {
        // Modo Simples: apenas Fornecedor e Valor direto
        const parsedSimpleAmount = parseFloat(customExpenseFormData.simple_amount || customExpenseFormData.base_price_per_unit || 0);
        if (isNaN(parsedSimpleAmount) || parsedSimpleAmount <= 0) {
          toast.error('Informe um valor válido maior que zero');
          return;
        }
        totalAmount = parsedSimpleAmount;
        itemsPayload = []; // Sem produtos vinculados
      }

      const costData = {
        cost_type: 'CUSTOM',
        supplier_id: parseInt(customExpenseFormData.supplier_id),
        description: customExpenseFormData.description,
        base_price_per_unit: totalAmount,
        expense_date: customExpenseFormData.due_date || null,
        order_period_start: customExpenseFormData.order_period_start || null,
        order_period_end: customExpenseFormData.order_period_end || null,
        category_id: null,
        subcategory: 'Despesa Personalizada',
        unit_of_measure: 'un',
        pricing_type: 'fixed',
        unit_calculation_type: 'piece',
        items: itemsPayload,
        fiscal_number: customExpenseFormData.fiscal_number || null,
        receipt_number: customExpenseFormData.fiscal_number || null,
        custom_data: {
          fiscal_number: customExpenseFormData.fiscal_number || '',
          fiscal_series: customExpenseFormData.fiscal_series || '',
          fiscal_key: customExpenseFormData.fiscal_key || '',
          emission_date: customExpenseFormData.emission_date || '',
          entry_date: customExpenseFormData.entry_date || '',
          files: customExpenseFiles.map(f => ({
            name: f.name,
            size: f.size,
            type: f.type,
            uploaded_at: new Date().toISOString()
          }))
        }
      };

      await costService.createCost(costData);
      toast.success(
        customExpenseMode === 'simple'
          ? 'Despesa personalizada criada com sucesso (modo direto)!'
          : 'Pedido e subpedido detalhado criados com sucesso!'
      );
      setShowCustomExpenseForm(false);
      setCustomExpenseFormData({
        supplier_id: '',
        description: '',
        base_price_per_unit: '',
        simple_amount: '',
        due_date: '',
        order_period_start: new Date().toISOString().split('T')[0],
        order_period_end: '',
        fiscal_number: '',
        fiscal_series: '',
        fiscal_key: '',
        emission_date: '',
        entry_date: ''
      });
      setCustomExpenseFiles([]);
      setCustomExpenseFiscalErrors({});
      setCustomExpenseItems([]);
      setCustomExpenseValidation({ isValid: false, itemCount: 0, totalAmount: 0 });
      loadCustomExpenses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao criar despesa personalizada');
      console.error(error);
    }
  };

  // Handlers para Subpedidos e Prazos
  const handleOpenSubOrders = async (cost) => {
    try {
      setSelectedCostForSubOrders(cost);
      setShowSubOrdersModal(true);
      setLoadingSubOrders(true);
      const res = await costService.getSubOrders(cost.id);
      setSubOrdersData(res.data || { cost, sub_orders: [], unassigned_items: [] });
    } catch (error) {
      toast.error('Erro ao carregar subpedidos');
      console.error(error);
    } finally {
      setLoadingSubOrders(false);
    }
  };

  const handleOpenNewSubOrder = (cost) => {
    setSelectedCostForSubOrders(cost);
    setNewSubOrderFormData({
      sub_order_number: '',
      description: '',
      sub_order_date: new Date().toISOString().split('T')[0]
    });
    setNewSubOrderItems([]);
    setNewSubOrderValidation({ isValid: false, itemCount: 0, totalAmount: 0 });
    setShowNewSubOrderModal(true);
  };

  const handleSaveNewSubOrder = async (e) => {
    e.preventDefault();
    if (!selectedCostForSubOrders) return;
    if (newSubOrderItems.length === 0) {
      toast.error('Adicione pelo menos um item ao subpedido');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        sub_order_number: newSubOrderFormData.sub_order_number || undefined,
        description: newSubOrderFormData.description || undefined,
        sub_order_date: newSubOrderFormData.sub_order_date || undefined,
        items: newSubOrderItems.map(item => ({
          variant_id: item.variant_id,
          product_id: item.product_id,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price)
        }))
      };

      await costService.addSubOrder(selectedCostForSubOrders.id, payload);
      toast.success('Novo subpedido adicionado com sucesso!');
      setShowNewSubOrderModal(false);
      loadCustomExpenses();
      if (showSubOrdersModal) {
        handleOpenSubOrders(selectedCostForSubOrders);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao adicionar subpedido');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConfirmStock = (costId, subOrder) => {
    setConfirmStockData({
      costId,
      subOrderId: subOrder.id,
      subOrderNumber: subOrder.sub_order_number,
      warehouse_id: warehouses.length > 0 ? warehouses[0].id : '',
      observations: ''
    });
    setShowConfirmStockModal(true);
  };

  const handleExecuteConfirmStock = async (e) => {
    e.preventDefault();
    if (!confirmStockData.warehouse_id) {
      toast.error('Selecione um armazém de destino');
      return;
    }
    try {
      setConfirmingStockLoading(true);
      await costService.confirmSubOrderStock(
        confirmStockData.costId,
        confirmStockData.subOrderId,
        {
          warehouse_id: parseInt(confirmStockData.warehouse_id),
          observations: confirmStockData.observations || undefined
        }
      );
      toast.success('Entrada no estoque confirmada com sucesso!');
      setShowConfirmStockModal(false);
      loadCustomExpenses();
      if (selectedCostForSubOrders) {
        handleOpenSubOrders(selectedCostForSubOrders);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao confirmar entrada no estoque');
      console.error(error);
    } finally {
      setConfirmingStockLoading(false);
    }
  };

  const handleToggleOrderStatus = async (cost) => {
    const newStatus = cost.order_status === 'CLOSED' ? 'OPEN' : 'CLOSED';
    try {
      await costService.updateCustomOrderStatus(cost.id, { order_status: newStatus });
      toast.success(newStatus === 'OPEN' ? 'Pedido reaberto para novos itens!' : 'Pedido encerrado com sucesso!');
      loadCustomExpenses();
      if (selectedCostForSubOrders && selectedCostForSubOrders.id === cost.id) {
        handleOpenSubOrders(cost);
      }
    } catch (error) {
      toast.error('Erro ao alterar status do pedido');
      console.error(error);
    }
  };

  const handleEdit = (cost) => {
    setEditingCost(cost);

    // Helper para formatar data para input type="date" (YYYY-MM-DD)
    const formatDateForInput = (dateStr) => {
      if (!dateStr) return '';
      try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        return d.toISOString().split('T')[0];
      } catch {
        return '';
      }
    };

    setFormData({
      cost_type: cost.cost_type || 'product_cost',
      category_id: cost.category_id || '',
      subcategory: cost.subcategory || '',
      supplier_id: cost.supplier_id || '',
      alternative_supplier_id: cost.alternative_supplier_id || '',
      unit_id: cost.unit_id || '',
      description: cost.description || '',
      base_price_per_unit: (cost.base_price_per_unit || cost.unit_cost || 0).toString(),
      pricing_type: cost.pricing_type || 'dynamic',
      unit_calculation_type: cost.unit_calculation_type || 'piece',
      default_thickness_mm: cost.default_thickness_mm || cost.thickness || '',
      default_color: cost.default_color || cost.color || '',
      default_width_cm: cost.default_width_cm || cost.size_width || '',
      default_height_cm: cost.default_height_cm || cost.size_height || '',
      default_length_cm: cost.default_length_cm || cost.size_length || '',
      default_brand: cost.default_brand || cost.brand || '',
      default_model: cost.default_model || cost.model || '',
      min_order_quantity: cost.min_order_quantity || 1,
      lead_time_days: cost.lead_time_days || 0,
      observations: cost.observations || '',
      custom_data: cost.custom_data || {},
      expense_date: formatDateForInput(cost.expense_date) || formatDateForInput(cost.next_due_date),
      responsible_person: cost.responsible_person || '',
      selected_employees: [],
      unit_value: '',
      vehicle: cost.vehicle || '',
      km_odometer: cost.km_odometer ? cost.km_odometer.toString() : '',
      receipt_number: cost.receipt_number || '',
      payment_method: cost.payment_method || '',
      is_reimbursable: cost.is_reimbursable || false,
      reimbursement_status: cost.reimbursement_status || 'not_applicable',
      // Campos de recorrência
      recurrence_type: cost.recurrence_type || 'none',
      recurrence_day: cost.recurrence_day || '',
      recurrence_end_date: formatDateForInput(cost.recurrence_end_date),
      next_due_date: formatDateForInput(cost.next_due_date),
      is_auto_sync: cost.is_auto_sync || false
    });
    setShowForm(true);
  };

  // Buscar parcelas da despesa recorrente (quando editingCost tem recurrence_type != 'none')
  const fetchParcels = async (costId) => {
    try {
      const response = await costService.getRecurringCostParcels(costId);
      setParcelsList(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar parcelas:', error);
      setParcelsList([]);
    }
  };

  // Mutation para confirmar pagamento de parcela
  const confirmParcelMutation = useMutation({
    mutationFn: ({ parcelId, payment_method }) => costService.confirmRecurringCost(parcelId, { payment_method }),
    onSuccess: () => {
      toast.success('Pagamento confirmado com sucesso!');
      if (editingCost) {
        fetchParcels(editingCost.id);
      }
      queryClient.invalidateQueries(['company-cashbox-balance']);
      queryClient.invalidateQueries(['cashbox-transactions']);
    },
    onError: (error) => {
      toast.error(error?.message || 'Erro ao confirmar pagamento');
    }
  });

  const handleConfirmParcel = (parcel) => {
    const defaultMethod = editingCost?.payment_method || 'pix';
    setConfirmParcel({ show: true, parcel, payment_method: defaultMethod });
  };

  const handleConfirmParcelAction = () => {
    if (confirmParcel.parcel) {
      confirmParcelMutation.mutate({
        parcelId: confirmParcel.parcel.id,
        payment_method: confirmParcel.payment_method
      });
    }
    setConfirmParcel({ show: false, parcel: null, payment_method: 'pix' });
  };

  // Toggle mostrar parcelas
  const toggleParcelsSection = (cost) => {
    if (showParcels && editingCost?.id === cost.id) {
      setShowParcels(false);
      setParcelsList([]);
    } else {
      setEditingCost(cost);
      setShowParcels(true);
      fetchParcels(cost.id);
    }
  };

  // Toggle expandir linha com rateio de equipe
  const toggleCostExpansion = (costId) => {
    setExpandedCosts(prev => ({
      ...prev,
      [costId]: !prev[costId]
    }));
  };

  const handleDelete = async (id) => {
    setConfirmDelete({ show: true, costId: id });
  };

  const handleConfirmDeleteAction = async () => {
    const id = confirmDelete.costId;
    if (!id) return;
    try {
      await costService.deleteCost(id);
      loadCosts();
      // Invalidar queries relacionadas ao financeiro
      queryClient.invalidateQueries(['finance-stats']);
      queryClient.invalidateQueries(['finance-expenses']);
      queryClient.invalidateQueries(['finance-forecast']);
      queryClient.invalidateQueries(['finance-forecasted-expenses']);
    } catch (error) {
      setError('Erro ao remover custo');
      console.error(error);
    }
    setConfirmDelete({ show: false, costId: null });
  };

  const handleConfirmDeleteCustomCostAction = async () => {
    const cost = confirmDeleteCustomCost.cost;
    if (!cost) return;
    try {
      await costService.deleteCost(cost.id);
      toast.success('Despesa personalizada excluída com sucesso!');
      setConfirmDeleteCustomCost({ show: false, cost: null });
      loadCustomExpenses();
      loadCosts();
      queryClient.invalidateQueries(['finance-stats']);
      queryClient.invalidateQueries(['finance-expenses']);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao remover despesa personalizada');
      console.error(error);
      setConfirmDeleteCustomCost({ show: false, cost: null });
    }
  };

  // Toggle para seleção de custo individual
  const toggleCostSelection = (id) => {
    setSelectedCosts(prev => {
      if (prev.includes(id)) {
        return prev.filter(costId => costId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Selecionar todos os custos visíveis
  const toggleSelectAll = () => {
    const visibleCosts = costs
      .filter(cost => {
        if (activeTab === 'company') {
return cost.expense_category !== 'team';
        }
        return true;
      })
      .map(cost => cost.id);

    if (selectedCosts.length === visibleCosts.length && selectedCosts.length > 0) {
      setSelectedCosts([]);
    } else {
      setSelectedCosts(visibleCosts);
    }
  };

  // Bulk delete para custos selecionados
  const handleBulkDelete = async () => {
    if (selectedCosts.length === 0) {
      alert('Nenhum custo selecionado');
      return;
    }

    setConfirmBulkDelete({ show: true });
  };

  const handleConfirmBulkDeleteAction = async () => {
    try {
      await costService.bulkDeleteCosts(selectedCosts);
      setSelectedCosts([]);
      loadCosts();
      // Invalidar queries relacionadas ao financeiro
      queryClient.invalidateQueries(['finance-stats']);
      queryClient.invalidateQueries(['finance-expenses']);
      queryClient.invalidateQueries(['finance-forecast']);
      queryClient.invalidateQueries(['finance-forecasted-expenses']);
      alert(`${selectedCosts.length} custo(s) removido(s) com sucesso!`);
    } catch (error) {
      setError('Erro ao remover custos');
      console.error(error);
    }
    setConfirmBulkDelete({ show: false });
  };

  const resetForm = () => {
    // Definir valores padrão baseados na aba ativa
    let defaultCostType = 'product_cost';
    let defaultResponsible = '';
    let defaultExpenseCategory = '';

    if (activeTab === 'team') {
      defaultCostType = 'operational_expense';
      defaultExpenseCategory = 'team';
    } else if (activeTab === 'company') {
      defaultCostType = 'operational_expense';
      defaultExpenseCategory = 'company';
      defaultResponsible = 'EMPRESA';
    } else if (activeTab === 'tax') {
      defaultCostType = 'operational_expense';
      defaultExpenseCategory = 'tax';
      defaultResponsible = 'EMPRESA';
    }

    setFormData({
      cost_type: defaultCostType,
      expense_category: defaultExpenseCategory,
      category_id: '',
      subcategory: '',
      supplier_id: '',
      alternative_supplier_id: '',
      unit_id: '',
      description: '',
      base_price_per_unit: '',
      pricing_type: 'dynamic',
      unit_calculation_type: 'piece',
      default_thickness_mm: '',
      default_color: '',
      default_width_cm: '',
      default_height_cm: '',
      default_length_cm: '',
      default_brand: '',
      default_model: '',
      min_order_quantity: 1,
      lead_time_days: 0,
      observations: '',
      custom_data: {},
      expense_date: '',
      responsible_person: defaultResponsible,
      selected_employees: [],
      employee_ids: [],
      unit_value: '',
      vehicle: '',
      km_odometer: '',
      receipt_number: '',
      payment_method: '',
      is_reimbursable: false,
      reimbursement_status: 'not_applicable',
      // Campos de recorrência
      recurrence_type: 'none',
      recurrence_day: '',
      recurrence_end_date: '',
      next_due_date: '',
      is_auto_sync: false
    });
  };

  const handleOpenVariations = (cost) => {
    setSelectedCostForVariations(cost);
    setShowVariations(true);
  };

  const handleCloseVariations = () => {
    setShowVariations(false);
    setSelectedCostForVariations(null);
  };

  const handleOpenDynamicPricing = (cost) => {
    setSelectedCostForPricing(cost);
    setShowDynamicPricing(true);
  };

  const handleCloseDynamicPricing = () => {
    setShowDynamicPricing(false);
    setSelectedCostForPricing(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-2 sm:p-3 lg:p-4 w-full">
      {/* Header Melhorado e Agrupado */}
      <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-4 mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            Gestão de Custos
          </h1>
          <p className="text-sm text-gray-500 mt-1 font-medium">
            {filteredCosts.length} lançamentos encontrados
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Botões Principais de Lançamento */}
          <div className="flex flex-wrap items-center gap-2 bg-gray-50 p-1.5 rounded-lg border border-gray-200">
            <button
              onClick={() => { setActiveTab('company'); setShowForm(true); }}
              className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 shadow-sm rounded hover:bg-gray-50 hover:text-indigo-700 transition-all flex items-center gap-1.5"
            >
              🏢 Empresa
            </button>
            <button
              onClick={() => { setActiveTab('team'); setShowForm(true); }}
              className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 shadow-sm rounded hover:bg-gray-50 hover:text-orange-700 transition-all flex items-center gap-1.5"
            >
              👷 Equipe
            </button>
            <button
              onClick={() => { setActiveTab('tax'); setShowForm(true); }}
              className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 shadow-sm rounded hover:bg-gray-50 hover:text-red-700 transition-all flex items-center gap-1.5"
            >
              🏛️ Tributo
            </button>
            <button
              onClick={() => { setActiveTab('custom'); setShowForm(true); }}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-purple-600 shadow-sm rounded hover:bg-purple-700 transition-all flex items-center gap-1.5"
            >
              📦 Desp. Personalizada
            </button>
          </div>

          <div className="w-px h-8 bg-gray-200 hidden sm:block mx-1"></div>

          <button
            onClick={() => window.location.href = '/financeiro/categorias'}
            className="px-3 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 shadow-sm rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <FolderTree className="w-4 h-4" />
            Categorias
          </button>
          <button
            onClick={() => setShowMaterialWeights(true)}
            className="px-3 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 shadow-sm rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            Pesos de Material
          </button>
        </div>
      </div>

      {/* Tabs de Navegação - Visual mais Limpo */}
      <div className="flex border-b border-gray-200 mb-6 bg-white overflow-x-auto no-scrollbar font-medium rounded-t-xl">
        <button
          className={`py-3.5 px-6 text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${activeTab === 'general'
            ? 'border-primary-600 text-primary-700 bg-primary-50/50'
            : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          onClick={() => { setActiveTab('general'); setShowForm(false); }}
        >
          Visão Geral
        </button>
        <button
          className={`py-3.5 px-6 text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${activeTab === 'company'
            ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50'
            : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          onClick={() => { setActiveTab('company'); setShowForm(false); }}
        >
          🏢 Empresa
        </button>
        <button
          className={`py-3.5 px-6 text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${activeTab === 'team'
            ? 'border-orange-600 text-orange-700 bg-orange-50/50'
            : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          onClick={() => { setActiveTab('team'); setShowForm(false); }}
        >
          👷 Equipe
        </button>
        <button
          className={`py-3.5 px-6 text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${activeTab === 'tax'
            ? 'border-red-600 text-red-700 bg-red-50/50'
            : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          onClick={() => { setActiveTab('tax'); setShowForm(false); }}
        >
          🏛️ Tributos
        </button>
        <button
          className={`py-3.5 px-6 text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${activeTab === 'custom'
            ? 'border-purple-600 text-purple-700 bg-purple-50/50'
            : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          onClick={() => { setActiveTab('custom'); setShowForm(false); }}
        >
          📦 Carteira de Abates (Personalizadas)
        </button>
      </div>

      {/* Visualização de Tabela ou Formulário por Aba */}
      {!(activeTab === 'custom' || (showForm && !editingCost)) ? (
        <>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Filtros - Apenas mostrar se não estiver no modo formulário */}
          {!showForm && (
            <div className="bg-white p-4 rounded-xl mb-6 shadow-sm border border-gray-100 flex flex-wrap lg:flex-nowrap gap-3 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">
                  Categoria
                </label>
                <select
                  name="category_id"
                  value={filters.category_id}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:bg-white text-sm transition-colors"
                >
                  <option value="">Todas</option>
                  {categories
                    .filter(c => {
                      if (activeTab === 'company') {
                        return c.cost_scope === 'EMPRESA' || c.cost_scope === 'TODAS' || (!c.cost_scope && ['aluguel', 'infraestrutura', 'manutenção', 'impostos', 'marketing', 'serviços', 'energia', 'internet'].some(k => c.name.toLowerCase().includes(k)));
                      }
                      if (activeTab === 'team') {
                        return c.cost_scope === 'EQUIPE' || c.cost_scope === 'TODAS' || (!c.cost_scope && ['aliment', 'refeiç', 'transporte', 'vale', 'happy hour', 'viagem', 'diária', 'equipe'].some(k => c.name.toLowerCase().includes(k)));
                      }
                      if (activeTab === 'tax') {
                        return c.cost_scope === 'TRIBUTO' || c.cost_scope === 'TODAS' || (!c.cost_scope && ['inss', 'fgts', 'darf', 'gps', 'gfip', 'iss', 'icms', 'pis', 'cofins', 'irpj', 'csll', 'taxa', 'tribut', 'imposto'].some(k => c.name.toLowerCase().includes(k)));
                      }
                      return true;
                    })
                    .map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                </select>
              </div>

              {(activeTab === 'general' || activeTab === 'company') && (
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">
                    Fornecedor
                  </label>
                  <select
                    name="supplier_id"
                    value={filters.supplier_id}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:bg-white text-sm transition-colors"
                  >
                    <option value="">Todos</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex-1 min-w-[200px] lg:min-w-[250px]">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">
                  Buscar
                </label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    name="search"
                    value={filters.search}
                    onChange={handleSearchChange}
                    placeholder={activeTab === 'company' ? "Descrição da conta..." : activeTab === 'team' ? "Descrição da despesa..." : "Descrição, marca, modelo..."}
                    className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:bg-white text-sm transition-colors"
                  />
                </div>
              </div>

              <div className="flex-shrink-0 w-full sm:w-32">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">
                  Data Inicial
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={filters.start_date}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:bg-white text-sm transition-colors"
                />
              </div>

              <div className="flex-shrink-0 w-full sm:w-32">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">
                  Data Final
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={filters.end_date}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:bg-white text-sm transition-colors"
                />
              </div>

              <div className="flex-shrink-0 w-full sm:w-auto">
                <button
                  onClick={() => {
                    setFilters({ category_id: '', search: '', supplier_id: '', color: '', thickness: '', brand: '', start_date: '', end_date: '' });
                  }}
                  title="Limpar todos os filtros"
                  className="w-full px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-gray-900 transition-colors flex items-center justify-center gap-2 h-[38px]"
                >
                  <X className="w-4 h-4" />
                  <span className="sm:hidden">Limpar</span>
                </button>
              </div>
            </div>
          )}

          {/* Botões Superiores na aba Empresa/Equipe/Tributo */}
          {activeTab !== 'custom' && activeTab !== 'general' && !showForm && (
            <div className="flex flex-wrap items-center gap-3 mb-6 bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
              <span className="text-sm font-semibold text-gray-700 px-2">Ações Rápidas:</span>
              
              {/* Botão Unificado para criar despesa dentro da aba */}
              <button
                onClick={() => setShowForm(true)}
                className="px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 shadow-sm transition-all flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Nova Despesa de {
                  activeTab === 'company' ? 'Empresa' : 
                  activeTab === 'team' ? 'Equipe' : 'Tributo'
                }
              </button>

              <button
                onClick={() => window.location.href = '/financeiro/categorias'}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <FolderTree className="w-4 h-4 text-gray-500" />
                Gerenciar Categorias
              </button>
            </div>
          )}

          {/* CAIXA AZUL DE INFO PARA DESPESAS DA EMPRESA */}
          {activeTab === 'company' && showForm && (
            <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-indigo-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-indigo-700">
                    Use esta área para lançar contas fixas (Luz, Água, Aluguel), manutenções prediais e impostos.
                    Para despesas de funcionários (lanches, combustível), use a aba <strong>Despesas de Equipe</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Modal de Edição de Custo */}
          {showForm && editingCost && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800">
                    ✏️ Editar Custo
                  </h2>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setEditingCost(null);
                      resetForm();
                    }}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Categoria *
                        </label>
                        <select
                          name="category_id"
                          value={formData.category_id}
                          onChange={handleInputChange}
                          required
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Selecione uma categoria</option>
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Campos condicionais para DESPESAS OPERACIONAIS */}
                      {formData.cost_type === 'operational_expense' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Data da Despesa *
                            </label>
                            <input
                              type="date"
                              name="expense_date"
                              value={formData.expense_date}
                              onChange={handleInputChange}
                              required
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Responsável
                            </label>
                            <select
                              name="responsible_person"
                              value={formData.responsible_person}
                              onChange={handleInputChange}
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Selecione um funcionário</option>
                              {employees.map(employee => (
                                <option key={employee.id} value={employee.full_name}>
                                  {employee.full_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Veículo
                            </label>
                            <input
                              type="text"
                              name="vehicle"
                              value={formData.vehicle}
                              onChange={handleInputChange}
                              placeholder="Ex: Carro da empresa, Moto..."
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Quilometragem (km)
                            </label>
                            <input
                              type="number"
                              name="km_odometer"
                              value={formData.km_odometer}
                              onChange={handleInputChange}
                              placeholder="Ex: 50000"
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Número do Recibo/Nota
                            </label>
                            <input
                              type="text"
                              name="receipt_number"
                              value={formData.receipt_number}
                              onChange={handleInputChange}
                              placeholder="Número do recibo ou nota fiscal"
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Forma de Pagamento
                            </label>
                            <select
                              name="payment_method"
                              value={formData.payment_method}
                              onChange={handleInputChange}
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Selecione...</option>
                              <option value="dinheiro">Dinheiro</option>
                              <option value="pix">PIX</option>
                              <option value="cartao_debito">Cartão de Débito</option>
                              <option value="cartao_credito">Cartão de Crédito</option>
                              <option value="transferencia">Transferência Bancária</option>
                              <option value="cheque">Cheque</option>
                            </select>
                          </div>
                        </>
                      )}

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Descrição Detalhada *
                        </label>
                        <input
                          type="text"
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          required
                          placeholder="Ex: MDF 15mm cor Nogal, 1 chapa 275x185"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {formData.cost_type === 'operational_expense' ? 'Valor da Despesa (R$) *' : 'Preço de Custo (R$) *'}
                        </label>
                        <input
                          type="number"
                          name="base_price_per_unit"
                          value={formData.base_price_per_unit}
                          onChange={handleInputChange}
                          required
                          min="0"
                          step="0.01"
                          placeholder="0,00"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Observações
                        </label>
                        <textarea
                          name="observations"
                          value={formData.observations}
                          onChange={handleInputChange}
                          rows="2"
                          placeholder="Condições de pagamento, descontos..."
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => {
                          setShowForm(false);
                          setEditingCost(null);
                          resetForm();
                        }}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        Atualizar Custo
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Modal de Parcelas da Despesa Recorrente */}
          {showParcels && editingCost && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    📅 Parcelas da Despesa Recorrente
                    {editingCost.recurrence_type && editingCost.recurrence_type !== 'none' && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                        {editingCost.recurrence_type === 'monthly' && 'Mensal'}
                        {editingCost.recurrence_type === 'yearly' && 'Anual'}
                        {editingCost.recurrence_type === 'weekly' && 'Semanal'}
                        {editingCost.recurrence_type === 'daily' && 'Diário'}
                      </span>
                    )}
                  </h2>
                  <button
                    onClick={() => {
                      setShowParcels(false);
                      setParcelsList([]);
                      if (searchParams.get('openParcels')) {
                        const nextParams = new URLSearchParams(searchParams);
                        nextParams.delete('costId');
                        nextParams.delete('openParcels');
                        setSearchParams(nextParams, { replace: true });
                      }
                    }}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>
                <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
                  {parcelsList.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Nenhuma parcela encontrada</p>
                  ) : (
                    <div className="space-y-2">
                      {parcelsList.map((parcel) => {
                        const parseLocalDate = (dateStr) => {
                          if (!dateStr) return new Date();
                          const [year, month, day] = dateStr.split('T')[0].split('-');
                          return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                        };
                        const dueDate = parseLocalDate(parcel.due_date);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const isPast = dueDate < today;
                        const isPaid = parcel.status === 'paid';

                        return (
                          <div
                            key={parcel.id}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              isPaid
                                ? 'bg-blue-50 border-blue-200'
                                : isPast
                                  ? 'bg-red-50 border-red-200'
                                  : 'bg-green-50 border-green-200'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-3 h-3 rounded-full ${
                                isPaid ? 'bg-blue-500' : isPast ? 'bg-red-500' : 'bg-green-500'
                              }`} />
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {formatLocalDateBR(parcel.due_date)}
                                </p>
                                <p className={`text-xs ${
                                  isPaid ? 'text-blue-600' : isPast ? 'text-red-600' : 'text-green-600'
                                }`}>
                                  {isPaid
                                    ? `Pago em ${formatLocalDateBR(parcel.paid_at)}`
                                    : isPast
                                      ? 'Venceu'
                                      : 'Agendado'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="text-lg font-bold text-gray-900">
                                {formatCurrency(parcel.amount)}
                              </span>
                              {!isPaid && (
                                <button
                                  onClick={() => handleConfirmParcel(parcel)}
                                  disabled={confirmParcelMutation.isPending}
                                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-2xs"
                                >
                                  <Check className="h-4 w-4" />
                                  {confirmParcelMutation.isPending ? 'Confirmando...' : 'Confirmar'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Lista de Custos */}
          <div className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                Lista de Custos
              </h2>
              {selectedCosts.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Excluir ({selectedCosts.length})
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              {/* Desktop: Table View */}
              <table className="min-w-full hidden md:table">
                <thead className="bg-primary-50/50">
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                    <th className="pl-4 pr-2 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={
                          filteredCosts.length > 0 &&
                          selectedCosts.length === filteredCosts.length
                        }
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </th>
                    <th
                      onClick={() => handleSort('date')}
                      className="px-4 py-3 cursor-pointer hover:bg-primary-100/60 transition-colors select-none group"
                      title="Clique para ordenar por Data"
                    >
                      <div className="flex items-center gap-1.5">
                        <span className={sortConfig.key === 'date' ? 'font-bold text-primary-900' : ''}>Data</span>
                        {sortConfig.key === 'date' ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp size={14} className="text-primary-700" />
                          ) : (
                            <ArrowDown size={14} className="text-primary-700" />
                          )
                        ) : (
                          <ArrowUpDown size={14} className="text-gray-400 opacity-50 group-hover:opacity-100 transition-opacity" />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('category')}
                      className="px-4 py-3 cursor-pointer hover:bg-primary-100/60 transition-colors select-none group"
                      title="Clique para ordenar por Categoria"
                    >
                      <div className="flex items-center gap-1.5">
                        <span className={sortConfig.key === 'category' ? 'font-bold text-primary-900' : ''}>Categoria</span>
                        {sortConfig.key === 'category' ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp size={14} className="text-primary-700" />
                          ) : (
                            <ArrowDown size={14} className="text-primary-700" />
                          )
                        ) : (
                          <ArrowUpDown size={14} className="text-gray-400 opacity-50 group-hover:opacity-100 transition-opacity" />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('description')}
                      className="px-4 py-3 cursor-pointer hover:bg-primary-100/60 transition-colors select-none group"
                      title="Clique para ordenar por Descrição"
                    >
                      <div className="flex items-center gap-1.5">
                        <span className={sortConfig.key === 'description' ? 'font-bold text-primary-900' : ''}>Descrição</span>
                        {sortConfig.key === 'description' ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp size={14} className="text-primary-700" />
                          ) : (
                            <ArrowDown size={14} className="text-primary-700" />
                          )
                        ) : (
                          <ArrowUpDown size={14} className="text-gray-400 opacity-50 group-hover:opacity-100 transition-opacity" />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('cost')}
                      className="px-4 py-3 text-right cursor-pointer hover:bg-primary-100/60 transition-colors select-none group"
                      title="Clique para ordenar por Valor de Custo"
                    >
                      <div className="flex items-center justify-end gap-1.5">
                        <span className={sortConfig.key === 'cost' ? 'font-bold text-primary-900' : ''}>Custo</span>
                        {sortConfig.key === 'cost' ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp size={14} className="text-primary-700" />
                          ) : (
                            <ArrowDown size={14} className="text-primary-700" />
                          )
                        ) : (
                          <ArrowUpDown size={14} className="text-gray-400 opacity-50 group-hover:opacity-100 transition-opacity" />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('sale')}
                      className="px-4 py-3 text-right cursor-pointer hover:bg-primary-100/60 transition-colors select-none group"
                      title="Clique para ordenar por Valor de Venda"
                    >
                      <div className="flex items-center justify-end gap-1.5">
                        <span className={sortConfig.key === 'sale' ? 'font-bold text-primary-900' : ''}>Venda</span>
                        {sortConfig.key === 'sale' ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp size={14} className="text-primary-700" />
                          ) : (
                            <ArrowDown size={14} className="text-primary-700" />
                          )
                        ) : (
                          <ArrowUpDown size={14} className="text-gray-400 opacity-50 group-hover:opacity-100 transition-opacity" />
                        )}
                      </div>
                    </th>
                    <th className="pr-4 pl-2 py-3 text-right w-48">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredCosts
                    .slice(((currentPage - 1) * itemsPerPage), (currentPage * itemsPerPage))
                    .map((cost) => {
                      const costDate = cost.expense_date || cost.created_at;
                      return (
                      <React.Fragment key={cost.id}>
                        <tr className={`group hover:bg-primary-50/30 transition-colors ${selectedCosts.includes(cost.id) ? 'bg-primary-50' : ''}`}>
                          <td className="pl-4 py-4 w-10">
                            <input
                              type="checkbox"
                              checked={selectedCosts.includes(cost.id)}
                              onChange={() => toggleCostSelection(cost.id)}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">
                            {costDate ? formatLocalDateBR(costDate) : '-'}
                          </td>
                          <td className="px-4 py-4">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                              {cost.category_name || 'Sem categoria'}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <p className="text-sm font-medium text-gray-900">{cost.description}</p>
                            <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                              <span className="text-xs text-gray-500">{cost.subcategory || 'OPERACIONAL'} • {cost.unit_name || 'mensal'}</span>
                              {cost.payment_method && (
                                <span className={`inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-semibold border ${
                                  cost.payment_method.toLowerCase().includes('pix') ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                  cost.payment_method.toLowerCase().includes('dinheiro') ? 'bg-green-50 text-green-700 border-green-200' :
                                  cost.payment_method.toLowerCase().includes('cart') ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                  cost.payment_method.toLowerCase().includes('cheque') ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                  'bg-blue-50 text-blue-700 border-blue-200'
                                }`}>
                                  💳 {cost.payment_method.toUpperCase()}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right text-sm text-gray-600">
                            {cost.cost_type === 'operational_expense'
                              ? formatCurrency(cost.base_price_per_unit)
                              : formatCurrency(cost.unit_cost)}
                          </td>
                          <td className="px-4 py-4 text-right text-sm font-medium text-gray-900">
                            {cost.cost_type === 'CUSTOM' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                ABATIMENTO
                              </span>
                            ) : cost.cost_type === 'operational_expense' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                DESPESAS
                              </span>
                            ) : (
                              formatCurrency(cost.suggested_selling_price)
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-end gap-1">
                              {cost.team_allocation && (
                                <button
                                  onClick={() => toggleCostExpansion(cost.id)}
                                  className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                                  title="Rateio"
                                >
                                  {expandedCosts[cost.id] ? <span className="text-xs">▼</span> : <span className="text-xs">▶</span>}
                                </button>
                              )}
                              <button
                                onClick={() => handleEdit(cost)}
                                className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Editar"
                              >
                                <Edit2 size={16} />
                              </button>
                              {cost.recurrence_type && cost.recurrence_type !== 'none' ? (
                                <button
                                  onClick={() => toggleParcelsSection(cost)}
                                  className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                                  title="Ver Parcelas/Recorrência"
                                >
                                  <Calendar size={16} />
                                </button>
                              ) : cost.cost_type === 'product_cost' && !cost.recurrence_type ? (
                                <>
                                  <button
                                    onClick={() => handleOpenVariations(cost)}
                                    className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                                    title="Gerenciar Variações"
                                  >
                                    <Layers size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleOpenDynamicPricing(cost)}
                                    className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                                    title="Gerenciar Preços"
                                  >
                                    <DollarSign size={16} />
                                  </button>
                                </>
                              ) : null}
                              <button
                                onClick={() => handleDelete(cost.id)}
                                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remover"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {cost.team_allocation && expandedCosts[cost.id] && (
                          <tr className="bg-primary-50/50">
                            <td colSpan={7} className="px-4 py-4">
                              <div className="flex items-start gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-3">
                                    <Users className="w-5 h-5 text-primary-600" />
                                    <span className="font-bold text-primary-700">Rateio em Equipe</span>
                                  </div>
                                  <div className="grid grid-cols-3 gap-4 mb-4">
                                    <div className="bg-white rounded-lg p-3 border border-primary-200">
                                      <p className="text-xs text-primary-500 uppercase">Total</p>
                                      <p className="font-bold text-primary-700">
                                        R$ {cost.team_allocation.total_value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </p>
                                    </div>
                                    <div className="bg-white rounded-lg p-3 border border-primary-200">
                                      <p className="text-xs text-primary-500 uppercase">Pessoas</p>
                                      <p className="font-bold text-primary-700">{cost.team_allocation.employee_count}</p>
                                    </div>
                                    <div className="bg-white rounded-lg p-3 border border-primary-200">
                                      <p className="text-xs text-primary-500 uppercase">Por Pessoa</p>
                                      <p className="font-bold text-primary-700">
                                        R$ {cost.team_allocation.per_person_value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-primary-700 mb-2">Participantes:</p>
                                  <div className="bg-white rounded-lg border border-primary-200 p-3 max-h-40 overflow-y-auto">
                                    {cost.team_allocation.allocations?.map((emp, idx) => (
                                      <div key={emp.id} className="flex justify-between items-center text-sm py-1">
                                        <span className="flex items-center gap-2">
                                          <span className="text-primary-400">{idx + 1}.</span>
                                          <span className="text-gray-700">{emp.name}</span>
                                        </span>
                                        <span className="font-medium text-primary-600">
                                          R$ {emp.value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                    })}
                </tbody>
              </table>

              {/* Mobile: Barra de Ordenação */}
              <div className="p-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between gap-2 md:hidden">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                  <ArrowUpDown size={14} className="text-primary-600" /> Ordenar:
                </span>
                <select
                  value={`${sortConfig.key}-${sortConfig.direction}`}
                  onChange={(e) => {
                    const [key, direction] = e.target.value.split('-');
                    setSortConfig({ key, direction });
                    setCurrentPage(1);
                  }}
                  className="text-xs bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="date-desc">Data (Mais recente)</option>
                  <option value="date-asc">Data (Mais antiga)</option>
                  <option value="category-asc">Categoria (A-Z)</option>
                  <option value="category-desc">Categoria (Z-A)</option>
                  <option value="description-asc">Descrição (A-Z)</option>
                  <option value="description-desc">Descrição (Z-A)</option>
                  <option value="cost-desc">Custo (Maior primeiro)</option>
                  <option value="cost-asc">Custo (Menor primeiro)</option>
                  <option value="sale-desc">Venda (Maior primeiro)</option>
                  <option value="sale-asc">Venda (Menor primeiro)</option>
                </select>
              </div>

              {/* Mobile: Card View */}
              <div className="grid grid-cols-1 gap-3 p-3 md:hidden">
                {filteredCosts
                  .slice(((currentPage - 1) * itemsPerPage), (currentPage * itemsPerPage))
                  .map((cost) => (
                    <div
                      key={cost.id}
                      className={`bg-white rounded-lg border shadow-sm p-4 ${selectedCosts.includes(cost.id) ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-1">
                            {cost.category_name || 'Sem categoria'}
                          </span>
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {cost.description}
                          </h3>
                          <p className="text-xs text-gray-500 truncate">
                            {cost.subcategory} • {cost.unit_name}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={selectedCosts.includes(cost.id)}
                          onChange={() => toggleCostSelection(cost.id)}
                          className="ml-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                        />
                      </div>

                      {/* Details Row */}
                      <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-3">
                        {cost.supplier_name && (
                          <span className="bg-gray-100 px-2 py-1 rounded">
                            🏭 {cost.supplier_name}
                          </span>
                        )}
                        {cost.color && (
                          <span className="bg-gray-100 px-2 py-1 rounded">
                            🎨 {cost.color}
                          </span>
                        )}
                        {cost.thickness && (
                          <span className="bg-gray-100 px-2 py-1 rounded">
                            📏 {cost.thickness}mm
                          </span>
                        )}
                      </div>

                      {/* Prices */}
                      <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
                        <div>
                          <p className="text-xs text-gray-500">Custo</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {cost.cost_type === 'operational_expense' 
                              ? formatCurrency(cost.base_price_per_unit) 
                              : formatCurrency(cost.unit_cost)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Venda</p>
                          {cost.cost_type === 'CUSTOM' ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                              ABATIMENTO
                            </span>
                          ) : cost.cost_type === 'operational_expense' ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                              DESPESAS
                            </span>
                          ) : (
                            <p className="text-sm font-bold text-green-600">
                              {formatCurrency(cost.suggested_selling_price)}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleEdit(cost)}
                          className="flex-1 min-w-[80px] text-center px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200 transition-colors"
                        >
                          ✏️ Editar
                        </button>
                        {cost.recurrence_type && cost.recurrence_type !== 'none' && (
                          <button
                            onClick={() => toggleParcelsSection(cost)}
                            className="flex-1 min-w-[80px] text-center px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-200 transition-colors"
                          >
                            📅 Parcelas
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(cost.id)}
className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors"
                        >
                          <span className="text-sm"><X size={16} /></span>
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
              {filteredCosts.length > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredCosts.length)} de {filteredCosts.length} itens
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Anterior
                    </button>
                    {Array.from({ length: Math.ceil(filteredCosts.length / itemsPerPage) }, (_, i) => i + 1).slice(Math.max(0, currentPage - 3), Math.min(Math.ceil(filteredCosts.length / itemsPerPage), currentPage + 2)).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded border ${page === currentPage ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 hover:bg-gray-50'}`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredCosts.length / itemsPerPage), p + 1))}
                      disabled={currentPage >= Math.ceil(filteredCosts.length / itemsPerPage)}
                      className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              )}
            </div>
            {filteredCosts.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Nenhum custo encontrado com os filtros aplicados.
              </div>
            )}
          </div>
        </>
      ) : activeTab === 'company' || activeTab === 'general' ? (
        <div className="bg-white p-6 rounded-lg shadow mb-6 border-t-4 border-indigo-500">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                🏢 Lançamento de Despesa da Empresa
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                Lance contas fixas (Luz, Água, Aluguel), manutenções e impostos.
              </p>
            </div>
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 shadow-sm flex items-center justify-center gap-2 transition-all"
              >
                <Plus className="w-4 h-4" />
                Nova Despesa da Empresa
              </button>
            )}
          </div>

          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              setLoading(true);
              const isRecurring = formData.recurrence_type && formData.recurrence_type !== 'none';

              if (isRecurring) {
                // Usar createRecurringCost para despesas recorrentes
                const recurringData = {
                  description: formData.description,
                  amount: parseFloat(formData.base_price_per_unit || formData.unit_cost || 0),
                  expense_category: 'company',
                  category_id: formData.category_id ? parseInt(formData.category_id, 10) : null,
                  supplier_id: formData.supplier_id ? parseInt(formData.supplier_id, 10) : null,
                  payment_method: formData.payment_method || null,
                  receipt_number: formData.receipt_number || null,
                  expense_date: formData.expense_date || formData.next_due_date || new Date().toISOString().split('T')[0],
                  recurrence_type: formData.recurrence_type,
                  recurrence_day: formData.recurrence_day ? parseInt(formData.recurrence_day, 10) : null,
                  next_due_date: formData.next_due_date || formData.expense_date || new Date().toISOString().split('T')[0],
                  recurrence_end_date: formData.recurrence_end_date || null,
                  observations: formData.observations || null
                };
                await costService.createRecurringCost(recurringData);
                toast.success('Despesa recorrente criada com sucesso!');
              } else {
                // Usar createCost para despesas avulsas
                const costData = {
                  ...formData,
                  cost_type: 'operational_expense',
                  expense_category: 'company',
                  responsible_person: 'EMPRESA',
                  base_price_per_unit: parseFloat(formData.base_price_per_unit || formData.unit_cost || 0),
                  category_id: formData.category_id ? parseInt(formData.category_id, 10) : null,
                  supplier_id: formData.supplier_id ? parseInt(formData.supplier_id, 10) : null,
                };
                await costService.createCost(costData);
                toast.success('Despesa da empresa lançada com sucesso!');
              }
              resetForm();
              setShowForm(false);
              loadCosts();
              queryClient.invalidateQueries(['finance-stats']);
              queryClient.invalidateQueries(['finance-expenses']);
              queryClient.invalidateQueries(['finance-forecast']);
              queryClient.invalidateQueries(['finance-forecasted-expenses']);
            } catch (error) {
              console.error('Erro ao lançar despesa:', error);
              toast.error('Erro ao lançar despesa: ' + (error?.response?.data?.message || error?.message || 'Erro desconhecido'));
            } finally {
              setLoading(false);
            }
          }} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data da Despesa *</label>
                  <input
                    type="date"
                    name="expense_date"
                    value={formData.expense_date}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Selecione...</option>
                    {categories
                      .filter(c => c.cost_scope === 'EMPRESA' || c.cost_scope === 'TODAS' || (!c.cost_scope && ['aluguel', 'infraestrutura', 'manutenção', 'impostos', 'marketing', 'serviços', 'internet', 'energia'].some(k => c.name.toLowerCase().includes(k))))
                      .map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor (Opcional)</label>
                  <select
                    name="supplier_id"
                    value={formData.supplier_id}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Selecione...</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento (Opcional)</label>
                  <select
                    name="payment_method"
                    value={formData.payment_method}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Selecione...</option>
                    <option value="dinheiro">Dinheiro</option>
                    <option value="pix">PIX</option>
                    <option value="boleto">Boleto</option>
                    <option value="transferencia">Transferência</option>
                    <option value="cartao_credito">Cartão de Crédito</option>
                    <option value="debito_automatico">Débito Automático</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Ex: Conta de Luz Março/2026"
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor (R$) *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">R$</span>
                    <input
                      type="number"
                      name="base_price_per_unit"
                      value={formData.base_price_per_unit}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                      className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-bold text-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nº Recibo/Nota (Opcional)</label>
                  <input
                    type="text"
                    name="receipt_number"
                    value={formData.receipt_number}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    name="observations"
                    value={formData.observations}
                    onChange={handleInputChange}
                    rows="2"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Toggle Recorrente / Avulsa */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-6 mb-4">
                <span className="text-sm font-medium text-gray-700">Tipo de Lançamento:</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="is_recurring_form"
                    checked={formData.recurrence_type === 'none'}
                    onChange={() => setFormData(prev => ({ ...prev, recurrence_type: 'none', next_due_date: '', recurrence_day: '', recurrence_end_date: '' }))}
                    className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-semibold text-gray-700">Avulsa (Única)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="is_recurring_form"
                    checked={formData.recurrence_type !== 'none'}
                    onChange={() => {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      const formatted = tomorrow.toISOString().split('T')[0];
                      setFormData(prev => ({
                        ...prev,
                        recurrence_type: prev.recurrence_type === 'none' ? 'monthly' : prev.recurrence_type,
                        recurrence_day: new Date().getDate(),
                        next_due_date: prev.next_due_date || formatted,
                        recurrence_end_date: prev.recurrence_end_date || ''
                      }));
                    }}
                    className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-semibold text-gray-700">Recorrente (Mensal/Anual)</span>
                </label>
              </div>

              {formData.recurrence_type !== 'none' && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Recorrência</label>
                    <select
                      name="recurrence_type"
                      value={formData.recurrence_type}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="daily">Diária</option>
                      <option value="weekly">Semanal</option>
                      <option value="monthly">Mensal</option>
                      <option value="yearly">Anual</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dia do Vencimento</label>
                    <input
                      type="number"
                      name="recurrence_day"
                      value={formData.recurrence_day}
                      onChange={handleInputChange}
                      min="1"
                      max="31"
                      placeholder="1-31"
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Primeiro Vencimento</label>
                    <input
                      type="date"
                      name="next_due_date"
                      value={formData.next_due_date}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fim da Recorrência</label>
                    <input
                      type="date"
                      name="recurrence_end_date"
                      value={formData.recurrence_end_date}
                      onChange={handleInputChange}
                      placeholder="Opcional"
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="pt-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowForm(false); resetForm(); }}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 shadow-sm flex items-center gap-2 disabled:opacity-50 transition-all"
              >
                <Save className="w-4 h-4" />
                {loading ? 'Salvando...' : 'Lançar Despesa'}
              </button>
            </div>
          </form>
        </div>
      ) : activeTab === 'tax' ? (
        <div className="bg-white p-6 rounded-lg shadow mb-6 border-t-4 border-red-500">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                🏛️ Lançamento de Despesa Tributária
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                Lane pagamentos de INSS, FGTS, DARF, GPS, GFIP e outros tributos.
              </p>
            </div>
          </div>

          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              setLoading(true);
              const isRecurring = formData.recurrence_type && formData.recurrence_type !== 'none';

              if (isRecurring) {
                const recurringData = {
                  description: formData.description,
                  amount: parseFloat(formData.base_price_per_unit || formData.unit_cost || 0),
                  expense_category: 'company',
                  category_id: formData.category_id ? parseInt(formData.category_id, 10) : null,
                  supplier_id: formData.supplier_id ? parseInt(formData.supplier_id, 10) : null,
                  payment_method: formData.payment_method || null,
                  receipt_number: formData.receipt_number || null,
                  expense_date: formData.expense_date || formData.next_due_date || new Date().toISOString().split('T')[0],
                  recurrence_type: formData.recurrence_type,
                  recurrence_day: formData.recurrence_day ? parseInt(formData.recurrence_day, 10) : null,
                  next_due_date: formData.next_due_date || formData.expense_date || new Date().toISOString().split('T')[0],
                  recurrence_end_date: formData.recurrence_end_date || null,
                  observations: formData.observations || null
                };
                await costService.createRecurringCost(recurringData);
                toast.success('Despesa tributária recorrente criada com sucesso!');
              } else {
                const costData = {
                  ...formData,
                  cost_type: 'operational_expense',
                  expense_category: 'company',
                  responsible_person: 'EMPRESA',
                  base_price_per_unit: parseFloat(formData.base_price_per_unit || formData.unit_cost || 0),
                  category_id: formData.category_id ? parseInt(formData.category_id, 10) : null,
                  supplier_id: formData.supplier_id ? parseInt(formData.supplier_id, 10) : null,
                };
                await costService.createCost(costData);
                toast.success('Despesa tributária lançada com sucesso!');
              }
              resetForm();
              setShowForm(false);
              loadCosts();
              queryClient.invalidateQueries(['finance-stats']);
              queryClient.invalidateQueries(['finance-expenses']);
              queryClient.invalidateQueries(['finance-forecast']);
              queryClient.invalidateQueries(['finance-forecasted-expenses']);
            } catch (error) {
              console.error('Erro ao lançar despesa:', error);
              toast.error('Erro ao lançar despesa: ' + (error?.response?.data?.message || error?.message || 'Erro desconhecido'));
            } finally {
              setLoading(false);
            }
          }} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data da Despesa *</label>
                  <input
                    type="date"
                    name="expense_date"
                    value={formData.expense_date}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria do Imposto *</label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">Selecione o tributo...</option>
                    {categories
                      .filter(c => c.cost_scope === 'TRIBUTO' || c.cost_scope === 'TODAS' || ['inss', 'fgts', 'darf', 'gps', 'gfip', 'iss', 'icms', 'pis', 'cofins', 'irpj', 'csll', 'taxa', 'tribut', 'imposto', 'arrecadação'].some(t => c.name.toLowerCase().includes(t)))
                      .map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Ex: INSS GPS Março 2026"
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor (R$) *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">R$</span>
                    <input
                      type="number"
                      name="base_price_per_unit"
                      value={formData.base_price_per_unit}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                      className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 font-bold text-lg"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento</label>
                  <select
                    name="payment_method"
                    value={formData.payment_method}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">Selecione...</option>
                    <option value="boleto">Boleto</option>
                    <option value="transferencia">Transferência</option>
                    <option value="pix">PIX</option>
                    <option value="debito">Débito em Conta</option>
                    <option value="carteira">Carteira</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    name="observations"
                    value={formData.observations}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Código de barras, histórico, etc..."
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>

                {/* Toggle Recorrente / Avulsa */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-6 mb-4">
                    <span className="text-sm font-medium text-gray-700">Tipo de Lançamento:</span>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="is_recurring_form_tax"
                        checked={formData.recurrence_type === 'none'}
                        onChange={() => setFormData(prev => ({ ...prev, recurrence_type: 'none', next_due_date: '', recurrence_day: '', recurrence_end_date: '' }))}
                        className="w-4 h-4 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm font-semibold text-gray-700">Avulsa (Única)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="is_recurring_form_tax"
                        checked={formData.recurrence_type !== 'none'}
                        onChange={() => {
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          const formatted = tomorrow.toISOString().split('T')[0];
                          setFormData(prev => ({
                            ...prev,
                            recurrence_type: prev.recurrence_type === 'none' ? 'monthly' : prev.recurrence_type,
                            recurrence_day: new Date().getDate(),
                            next_due_date: prev.next_due_date || formatted,
                            recurrence_end_date: prev.recurrence_end_date || ''
                          }));
                        }}
                        className="w-4 h-4 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm font-semibold text-gray-700">Recorrente (Mensal/Anual)</span>
                    </label>
                  </div>

                  {formData.recurrence_type !== 'none' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-red-50 rounded-lg border border-red-100">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Recorrência</label>
                        <select
                          name="recurrence_type"
                          value={formData.recurrence_type}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        >
                          <option value="monthly">Mensal</option>
                          <option value="yearly">Anual</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Dia do Vencimento</label>
                        <input
                          type="number"
                          name="recurrence_day"
                          value={formData.recurrence_day}
                          onChange={handleInputChange}
                          min="1"
                          max="31"
                          placeholder="1-31"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Primeiro Vencimento</label>
                        <input
                          type="date"
                          name="next_due_date"
                          value={formData.next_due_date}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Rodapé do Formulário */}
            <div className="pt-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowForm(false); resetForm(); }}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 shadow-sm flex items-center gap-2 disabled:opacity-50 transition-all"
              >
                <Save className="w-4 h-4" />
                {loading ? 'Salvando...' : 'Lançar Despesa'}
              </button>
            </div>
          </form>
        </div>
      ) : activeTab === 'custom' ? (
        <div className="bg-white p-3 sm:p-5 rounded-xl shadow-xs mb-6 border-t-4 border-purple-500">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
            <div>
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                Despesas Personalizadas com Abates
              </h2>
              <p className="text-gray-600 text-sm mt-0.5">
                Despesas com abatimentos vinculados a projetos.
              </p>
            </div>
            {!showCustomExpenseForm && (
              <button
                onClick={() => setShowCustomExpenseForm(true)}
                className="w-full sm:w-auto px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 shadow-sm flex items-center justify-center gap-2 transition-all"
              >
                <Plus className="w-4 h-4" />
                Nova Despesa Personalizada
              </button>
            )}
          </div>

          {/* Formulário de Nova Despesa Personalizada */}
          {showCustomExpenseForm && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 sm:p-5 mb-6 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 pb-3 border-b border-purple-200">
                <div>
                  <h3 className="text-lg font-bold text-purple-900">Nova Despesa Personalizada</h3>
                  <p className="text-xs text-purple-700">Lance valores diretamente para abater em projetos ou detalhe produtos para estoque.</p>
                </div>
                {/* Seletor de Modo: Simples (Direto) vs Completo (Com Produtos) */}
                <div className="inline-flex bg-white rounded-lg p-1 border border-purple-300 shadow-sm self-start">
                  <button
                    type="button"
                    onClick={() => setCustomExpenseMode('simple')}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                      customExpenseMode === 'simple'
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-purple-700 hover:bg-purple-50'
                    }`}
                  >
                    💰 Modo Simples (Valor Direto)
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomExpenseMode('detailed')}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                      customExpenseMode === 'detailed'
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-purple-700 hover:bg-purple-50'
                    }`}
                  >
                    📦 Modo Completo (Com Produtos)
                  </button>
                </div>
              </div>

              <form onSubmit={handleCustomExpenseSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fornecedor *
                  </label>
                  <select
                    value={customExpenseFormData.supplier_id}
                    onChange={(e) => setCustomExpenseFormData(prev => ({ ...prev, supplier_id: e.target.value }))}
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">Selecione um fornecedor</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição *
                  </label>
                  <input
                    type="text"
                    value={customExpenseFormData.description}
                    onChange={(e) => setCustomExpenseFormData(prev => ({ ...prev, description: e.target.value }))}
                    required
                    placeholder="Ex: MDF para cliente X"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                {customExpenseMode === 'simple' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor Total do Pedido (R$) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      required
                      value={customExpenseFormData.simple_amount}
                      onChange={(e) => setCustomExpenseFormData(prev => ({ ...prev, simple_amount: e.target.value }))}
                      placeholder="Ex: 2500.00"
                      className="w-full p-2 border border-purple-300 bg-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-bold text-gray-900"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor do Pedido (R$) *
                    </label>
                    <div className="w-full p-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 font-medium">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(customExpenseValidation.totalAmount || 0)}
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Vencimento
                  </label>
                  <input
                    type="date"
                    value={customExpenseFormData.due_date}
                    onChange={(e) => setCustomExpenseFormData(prev => ({ ...prev, due_date: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                {/* Dados Fiscais (NF-e) */}
                <div className="md:col-span-2 lg:col-span-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-3">Dados Fiscais (NF-e)</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-blue-700 mb-1">Número NF</label>
                      <input
                        type="text"
                        value={customExpenseFormData.fiscal_number}
                        onChange={(e) => {
                          const val = e.target.value;
                          setCustomExpenseFormData(prev => ({ ...prev, fiscal_number: val }));
                          setCustomExpenseFiscalErrors(prev => ({
                            ...prev,
                            fiscal_number: val && !/^\d+$/.test(val) ? 'Apenas números' : ''
                          }));
                        }}
                        className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${customExpenseFiscalErrors.fiscal_number ? 'border-red-400' : 'border-blue-300'}`}
                        placeholder="12345"
                      />
                      {customExpenseFiscalErrors.fiscal_number && (
                        <p className="text-[11px] text-red-600 mt-1">{customExpenseFiscalErrors.fiscal_number}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-blue-700 mb-1">Série</label>
                      <input
                        type="text"
                        value={customExpenseFormData.fiscal_series}
                        onChange={(e) => {
                          const val = e.target.value;
                          setCustomExpenseFormData(prev => ({ ...prev, fiscal_series: val }));
                          setCustomExpenseFiscalErrors(prev => ({
                            ...prev,
                            fiscal_series: val && !/^\d+$/.test(val) ? 'Apenas números' : ''
                          }));
                        }}
                        className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${customExpenseFiscalErrors.fiscal_series ? 'border-red-400' : 'border-blue-300'}`}
                        placeholder="001"
                      />
                      {customExpenseFiscalErrors.fiscal_series && (
                        <p className="text-[11px] text-red-600 mt-1">{customExpenseFiscalErrors.fiscal_series}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-blue-700 mb-1">Chave NF-e</label>
                      <input
                        type="text"
                        value={customExpenseFormData.fiscal_key}
                        onChange={(e) => {
                          const val = e.target.value;
                          setCustomExpenseFormData(prev => ({ ...prev, fiscal_key: val }));
                          setCustomExpenseFiscalErrors(prev => ({
                            ...prev,
                            fiscal_key: val && val.length !== 44 ? 'Deve ter 44 dígitos' : ''
                          }));
                        }}
                        className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${customExpenseFiscalErrors.fiscal_key ? 'border-red-400' : 'border-blue-300'}`}
                        placeholder="3526..."
                      />
                      {customExpenseFiscalErrors.fiscal_key && (
                        <p className="text-[11px] text-red-600 mt-1">{customExpenseFiscalErrors.fiscal_key}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-blue-700 mb-1">Data Emissão</label>
                      <input
                        type="date"
                        value={customExpenseFormData.emission_date}
                        onChange={(e) => setCustomExpenseFormData(prev => ({ ...prev, emission_date: e.target.value }))}
                        className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-blue-700 mb-1">Data Entrada</label>
                      <input
                        type="date"
                        value={customExpenseFormData.entry_date}
                        onChange={(e) => setCustomExpenseFormData(prev => ({ ...prev, entry_date: e.target.value }))}
                        className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Anexos */}
                <div className="md:col-span-2 lg:col-span-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-emerald-900 mb-3">📎 Anexos</h4>
                  <div className="flex flex-wrap items-center gap-3">
                    <input
                      id="custom-expense-files-input"
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length) {
                          setCustomExpenseFiles(prev => [...prev, ...files]);
                        }
                      }}
                      className="hidden"
                    />
                    <label
                      htmlFor="custom-expense-files-input"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer bg-emerald-600 text-white hover:bg-emerald-700"
                    >
                      <Upload className="w-4 h-4" />
                      Adicionar Anexo
                    </label>
                    <p className="text-xs text-emerald-700">
                      Máx. 20 anexos | 50MB por arquivo | PDF, DOC, XLS, imagens, TXT
                    </p>
                  </div>

                  {customExpenseFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {customExpenseFiles.map((file, fIdx) => (
                        <div
                          key={fIdx}
                          className="flex items-center gap-3 p-2.5 bg-white border border-emerald-200 rounded-md"
                        >
                          <FileText className="w-5 h-5 text-emerald-600 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate" title={file.name}>
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(file.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setCustomExpenseFiles(prev => prev.filter((_, i) => i !== fIdx))}
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            title="Remover anexo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {customExpenseMode === 'simple' ? (
                  <div className="md:col-span-2 lg:col-span-4 bg-white/80 border border-purple-200 rounded-xl p-3.5 text-xs text-purple-950 flex items-start gap-3 shadow-xs">
                    <span className="text-xl">💡</span>
                    <div>
                      <p className="font-bold text-sm text-purple-950 mb-0.5">Modo Simples (Apenas Fornecedor e Valor)</p>
                      <p className="text-purple-800 leading-relaxed">
                        Este valor de <strong>{customExpenseFormData.simple_amount ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(customExpenseFormData.simple_amount) || 0) : 'R$ 0,00'}</strong> ficará disponível imediatamente para ser usado como <strong>abate de parcelas</strong> de clientes nos Projetos (Contas a Receber). Não é necessário vincular produtos nem movimentar almoxarifado.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="md:col-span-2 lg:col-span-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-purple-900">Itens do Subpedido Inicial (#1)</span>
                      <span className="text-xs text-gray-500">Você poderá adicionar mais subpedidos ao longo do período</span>
                    </div>
                    <PurchaseItemsGrid
                      items={customExpenseItems}
                      setItems={setCustomExpenseItems}
                      supplierId={customExpenseFormData.supplier_id}
                      onValidationChange={(isValid, itemCount, totalAmount) => {
                        setCustomExpenseValidation({ isValid, itemCount, totalAmount });
                        setCustomExpenseFormData(prev => ({ ...prev, base_price_per_unit: totalAmount.toString() }));
                      }}
                      errors={customExpenseItems.length === 0 ? { items: 'Adicione pelo menos um item' } : {}}
                    />
                  </div>
                )}

                <div className="md:col-span-2 lg:col-span-4 flex justify-end gap-3 mt-2 pt-4 border-t border-purple-100">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomExpenseForm(false);
                      setCustomExpenseFormData({
                        supplier_id: '',
                        description: '',
                        base_price_per_unit: '',
                        simple_amount: '',
                        due_date: '',
                        order_period_start: new Date().toISOString().split('T')[0],
                        order_period_end: '',
                        fiscal_number: '',
                        fiscal_series: '',
                        fiscal_key: '',
                        emission_date: '',
                        entry_date: ''
                      });
                      setCustomExpenseFiles([]);
                      setCustomExpenseFiscalErrors({});
                      setCustomExpenseItems([]);
                      setCustomExpenseValidation({ isValid: false, itemCount: 0, totalAmount: 0 });
                    }}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 text-sm font-semibold bg-purple-600 text-white rounded-lg hover:bg-purple-700 shadow-sm flex items-center gap-2 transition-all"
                  >
                    <Save className="w-4 h-4" />
                    {customExpenseMode === 'simple' ? 'Salvar Despesa para Abates' : 'Salvar Pedido Completo'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Mini-Dashboard de Métricas Financeiras e Carteira de Abates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-200/80 rounded-xl p-3.5 shadow-2xs">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-purple-900 uppercase tracking-wider">Total em Pedidos</span>
                <span className="p-1.5 bg-purple-100 text-purple-700 rounded-lg">
                  <DollarSign className="w-4 h-4" />
                </span>
              </div>
              <p className="text-lg font-bold text-gray-900">
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(customExpensesStats.totalAmount || 0)}
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {customExpensesStats.total} pedido{customExpensesStats.total !== 1 ? 's' : ''} personalizado{customExpensesStats.total !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200/80 rounded-xl p-3.5 shadow-2xs">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-emerald-900 uppercase tracking-wider">Total Já Abatido</span>
                <span className="p-1.5 bg-emerald-100 text-emerald-700 rounded-lg">
                  <ArrowDownRight className="w-4 h-4" />
                </span>
              </div>
              <p className="text-lg font-bold text-emerald-700">
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(customExpensesStats.totalAbated || 0)}
              </p>
              <p className="text-[11px] text-emerald-600/90 mt-0.5 font-medium">
                {customExpensesStats.totalAmount > 0
                  ? `${Math.round(((customExpensesStats.totalAbated || 0) / customExpensesStats.totalAmount) * 100)}% compensado em projetos`
                  : '0% compensado'}
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-200/80 rounded-xl p-3.5 shadow-2xs">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-amber-900 uppercase tracking-wider">Saldo a Abater</span>
                <span className="p-1.5 bg-amber-100 text-amber-700 rounded-lg">
                  <Clock className="w-4 h-4" />
                </span>
              </div>
              <p className="text-lg font-bold text-amber-700">
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(customExpensesStats.totalRemaining || 0)}
              </p>
              <p className="text-[11px] text-amber-600/90 mt-0.5 font-medium">
                Disponível para abatimento
              </p>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-3.5 shadow-2xs">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Status da Carteira</span>
                <span className="p-1.5 bg-slate-100 text-slate-600 rounded-lg">
                  <Layers className="w-4 h-4" />
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-md border border-green-200">
                  <CheckCircle2 className="w-3 h-3" /> {customExpensesStats.paid} quitado{customExpensesStats.paid !== 1 ? 's' : ''}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">
                  <AlertCircle className="w-3 h-3" /> {customExpensesStats.pending} pendente{customExpensesStats.pending !== 1 ? 's' : ''}
                </span>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                {customExpensesStats.partial} parcialmente compensado{customExpensesStats.partial !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Barra de Filtros e Busca de Despesas Personalizadas */}
          <div className="bg-gray-50/90 border border-gray-200/80 rounded-xl p-3 sm:p-3.5 mb-4 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-2xs">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <span className="text-xs font-bold text-gray-600 mr-1 hidden sm:inline">Filtrar por:</span>
              <button
                type="button"
                onClick={() => setCustomExpenseStatusFilter('ALL')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  customExpenseStatusFilter === 'ALL'
                    ? 'bg-purple-600 text-white shadow-xs'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <span>Todas</span>
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  customExpenseStatusFilter === 'ALL' ? 'bg-purple-800 text-white' : 'bg-gray-100 text-gray-700'
                }`}>
                  {customExpensesStats.total}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setCustomExpenseStatusFilter('PAID')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  customExpenseStatusFilter === 'PAID'
                    ? 'bg-green-600 text-white shadow-xs'
                    : 'bg-white text-green-800 hover:bg-green-50 border border-green-200'
                }`}
              >
                <span>✅ Já pagas</span>
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  customExpenseStatusFilter === 'PAID' ? 'bg-green-800 text-white' : 'bg-green-100 text-green-800'
                }`}>
                  {customExpensesStats.paid}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setCustomExpenseStatusFilter('PARTIAL')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  customExpenseStatusFilter === 'PARTIAL'
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'bg-white text-blue-800 hover:bg-blue-50 border border-blue-200'
                }`}
              >
                <span>⏳ Parcialmente pagas</span>
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  customExpenseStatusFilter === 'PARTIAL' ? 'bg-blue-800 text-white' : 'bg-blue-100 text-blue-800'
                }`}>
                  {customExpensesStats.partial}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setCustomExpenseStatusFilter('PENDING')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  customExpenseStatusFilter === 'PENDING'
                    ? 'bg-amber-600 text-white shadow-xs'
                    : 'bg-white text-amber-800 hover:bg-amber-50 border border-amber-200'
                }`}
              >
                <span>⚠️ Não pagas</span>
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  customExpenseStatusFilter === 'PENDING' ? 'bg-amber-800 text-white' : 'bg-amber-100 text-amber-800'
                }`}>
                  {customExpensesStats.pending}
                </span>
              </button>
            </div>

            <div className="relative min-w-[240px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={customExpenseSearchTerm}
                onChange={(e) => setCustomExpenseSearchTerm(e.target.value)}
                placeholder="Buscar fornecedor, pedido ou ID..."
                className="w-full pl-9 pr-7 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              {customExpenseSearchTerm && (
                <button
                  type="button"
                  onClick={() => setCustomExpenseSearchTerm('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {customExpensesLoading ? (
            <div className="text-center py-12 text-gray-500 flex flex-col items-center justify-center gap-2">
              <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-xs font-medium">Carregando despesas com abates...</span>
            </div>
          ) : customExpensesData.length === 0 ? (
            <div className="text-center py-12 text-gray-500 bg-gray-50/70 rounded-xl border border-gray-200/80">
              <FileText className="w-10 h-10 text-gray-400 mx-auto mb-2 opacity-60" />
              <p className="font-semibold text-gray-700">Nenhuma despesa personalizada cadastrada.</p>
              <p className="text-xs text-gray-500 mt-1">Clique em "Nova Despesa Personalizada" acima para começar.</p>
            </div>
          ) : filteredCustomExpenses.length === 0 ? (
            <div className="text-center py-12 text-gray-500 bg-gray-50/70 rounded-xl border border-gray-200/80">
              <Search className="w-10 h-10 text-gray-400 mx-auto mb-2 opacity-60" />
              <p className="font-semibold text-gray-700">Nenhuma despesa encontrada para os filtros selecionados.</p>
              <button
                type="button"
                onClick={() => { setCustomExpenseStatusFilter('ALL'); setCustomExpenseSearchTerm(''); }}
                className="mt-3 text-xs font-semibold text-purple-600 hover:text-purple-800 underline"
              >
                Limpar filtros de busca
              </button>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-xl shadow-2xs overflow-hidden bg-white">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse table-auto">
                  <thead className="bg-gray-50/90 border-b border-gray-200 text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                    <tr>
                      <th className="px-2.5 py-2.5 text-center w-28 whitespace-nowrap">Ações</th>
                      <th className="px-2.5 py-2.5 min-w-[140px]">Fornecedor</th>
                      <th className="px-2.5 py-2.5 min-w-[150px]">Pedido</th>
                      <th className="px-2 py-2.5 text-center min-w-[110px]">Data / Vigência</th>
                      <th className="px-2.5 py-2.5 text-right whitespace-nowrap">Total / Abatido</th>
                      <th className="px-2.5 py-2.5 text-right whitespace-nowrap">Restante</th>
                      <th className="px-2.5 py-2.5 text-center whitespace-nowrap">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white text-xs">
                  {filteredCustomExpenses.map((cost) => {
                    const basePrice = parseFloat(cost.base_price_per_unit || 0);
                    const abatedPrice = parseFloat(cost.total_abated || 0);
                    const remainingPrice = parseFloat(cost.remaining_amount !== undefined ? cost.remaining_amount : Math.max(0, basePrice - abatedPrice));
                    const percentageAbated = basePrice > 0 ? Math.min(100, Math.round((abatedPrice / basePrice) * 100)) : 0;
                    const paymentStatus = getCustomExpensePaymentStatus(cost);

                    return (
                      <tr key={cost.id} className="hover:bg-purple-50/20 transition-colors">
                        {/* 1. Coluna AÇÕES agora em primeiro lugar */}
                        <td className="px-2 py-2 text-center whitespace-nowrap">
                          <div className="flex items-center justify-center gap-1">
                            {/* Botão Primário: Ver Abates */}
                            <button
                              type="button"
                              onClick={async () => {
                                setSelectedCostAbates(cost);
                                setShowAbatesModal(true);
                                await loadCostAbates(cost.id);
                              }}
                              className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white text-[10px] font-semibold rounded shadow-2xs transition-all flex items-center gap-0.5"
                              title="Visualizar projetos e parcelas onde esta despesa foi abatida"
                            >
                              <ArrowDownRight className="w-3 h-3" />
                              <span>Abates</span>
                            </button>

                            {/* Botão Encerrar / Reabrir */}
                            <button
                              type="button"
                              onClick={() => handleToggleOrderStatus(cost)}
                              className="p-1 text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded transition-colors shadow-2xs"
                              title={cost.order_status === 'CLOSED' ? 'Reabrir pedido' : 'Encerrar pedido'}
                            >
                              {cost.order_status === 'CLOSED' ? (
                                <Unlock className="w-3 h-3 text-emerald-600" />
                              ) : (
                                <Lock className="w-3 h-3 text-gray-500" />
                              )}
                            </button>

                            {/* Botão Excluir (Restrito à Gerência e apenas se total_abated <= 0.01) */}
                            {isManager && parseFloat(cost.total_abated || 0) <= 0.01 && (
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteCustomCost({ show: true, cost })}
                                className="p-1 bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 border border-red-200 rounded transition-colors shadow-2xs"
                                title="Excluir despesa"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </td>

                        {/* 2. Fornecedor */}
                        <td className="px-2.5 py-2">
                          <div className="flex items-center gap-1.5">
                            <span className="p-1 bg-gray-100 text-gray-500 rounded shrink-0">
                              <Building2 className="w-3 h-3" />
                            </span>
                            <span className="font-semibold text-gray-900 truncate max-w-[150px]" title={cost.supplier_name}>
                              {cost.supplier_name || 'Fornecedor não identificado'}
                            </span>
                          </div>
                        </td>

                        {/* 3. Pedido com ID compacto */}
                        <td className="px-2.5 py-2">
                          <div className="flex flex-col">
                            <span className="font-bold text-gray-900 truncate max-w-[180px]" title={cost.description}>
                              {cost.description}
                            </span>
                            <span className="text-[10px] text-gray-500 font-mono">
                              #{cost.id}
                            </span>
                          </div>
                        </td>

                        {/* 4. Vigência */}
                        <td className="px-2 py-2 text-center">
                          <div className="flex flex-col items-center gap-0.5">
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.2 rounded-full text-[9px] font-semibold ${
                              cost.order_status === 'CLOSED'
                                ? 'bg-gray-100 text-gray-700 border border-gray-200'
                                : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            }`}>
                              {cost.order_status === 'CLOSED' ? 'Encerrado' : 'Aberto'}
                            </span>
                            <span className="text-gray-500 text-[10px] whitespace-nowrap">
                              {cost.order_period_start ? formatLocalDateBR(cost.order_period_start) : '-'}
                              {cost.order_period_end ? ` a ${formatLocalDateBR(cost.order_period_end)}` : ''}
                            </span>
                          </div>
                        </td>



                        {/* 6. Total e Abatido consolidados */}
                        <td className="px-2.5 py-2 text-right whitespace-nowrap">
                          <div className="flex flex-col items-end">
                            <span className="font-bold text-gray-900 text-xs">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(basePrice)}
                            </span>
                            <span className="text-[10px] text-emerald-600 font-medium">
                              Abat.: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(abatedPrice)}
                            </span>
                          </div>
                        </td>

                        {/* 7. Restante */}
                        <td className="px-2.5 py-2 text-right whitespace-nowrap">
                          <span className={`font-bold text-xs ${
                            remainingPrice <= 0.01 ? 'text-gray-400 font-normal' : 'text-amber-700 font-extrabold'
                          }`}>
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(remainingPrice)}
                          </span>
                        </td>

                        {/* 8. Status */}
                        <td className="px-2 py-2 text-center whitespace-nowrap">
                          {paymentStatus === 'PAID' ? (
                            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-800 border border-green-200">
                              <CheckCircle2 className="w-2.5 h-2.5 text-green-600" /> 100%
                            </span>
                          ) : paymentStatus === 'PARTIAL' ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded-full text-[9px] font-bold bg-blue-50 text-blue-800 border border-blue-200">
                                <Clock className="w-2.5 h-2.5 text-blue-600" /> {percentageAbated}%
                              </span>
                              <div className="w-10 bg-gray-200 rounded-full h-1 overflow-hidden">
                                <div className="bg-blue-600 h-1 rounded-full" style={{ width: `${percentageAbated}%` }}></div>
                              </div>
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-50 text-amber-800 border border-amber-200">
                              <AlertCircle className="w-2.5 h-2.5 text-amber-600" /> Pendente
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow mb-6 border-t-4 border-orange-500">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                👥 Lançamento de Despesa em Equipe
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                Lance uma despesa única para múltiplos funcionários (ex: Lanche, Jantar, Combustível)
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="bg-orange-50 text-orange-700 px-4 py-2 rounded-lg text-sm font-medium">
                Modo: {teamExpenseMode === 'total' ? '💰 Valor Total (Rateio)' : '👤 Valor Individual'}
              </div>
              {!showForm && (
                <button
                  onClick={() => setShowForm(true)}
                  className="w-full sm:w-auto px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 shadow-sm flex items-center justify-center gap-2 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Nova Despesa de Equipe
                </button>
              )}
            </div>
          </div>

          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!formData.selected_employees || formData.selected_employees.length === 0) {
              toast.error('Selecione pelo menos um funcionário');
              return;
            }
            if (!formData.base_price_per_unit || parseFloat(formData.base_price_per_unit) <= 0) {
              toast.error('Informe um valor válido');
              return;
            }

            try {
              setLoading(true);
              const totalValue = parseFloat(formData.base_price_per_unit);

              // Criar UM ÚNICO registro com rateio detalhado
              const costData = {
                ...formData,
                cost_type: 'operational_expense',
                expense_category: 'team',
                team_mode: teamExpenseMode,
                selected_employees: formData.selected_employees,
                base_price_per_unit: totalValue,
                description: formData.description,
                responsible_person: user?.full_name || 'EMPRESA',
                supplier_id: formData.supplier_id ? parseInt(formData.supplier_id, 10) : null,
                unit_id: formData.unit_id ? parseInt(formData.unit_id, 10) : null,
                category_id: formData.category_id ? parseInt(formData.category_id, 10) : null,
                km_odometer: formData.km_odometer ? parseInt(formData.km_odometer, 10) : null,
              };

              await costService.createCost(costData);

              toast.success('Despesa em equipe lançada com sucesso!');
              resetForm();
              setShowForm(false);
              loadCosts();
              queryClient.invalidateQueries(['finance-stats']);
              queryClient.invalidateQueries(['finance-expenses']);
              queryClient.invalidateQueries(['finance-forecast']);
              queryClient.invalidateQueries(['finance-forecasted-expenses']);

            } catch (error) {
              console.error('Erro ao lançar despesas de equipe:', error);
              toast.error('Erro ao lançar despesas: ' + (error?.response?.data?.message || error?.message || 'Erro desconhecido'));
            } finally {
              setLoading(false);
            }
          }} className="space-y-6">

            {/* Seleção de Modo */}
            <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="teamMode"
                  checked={teamExpenseMode === 'total'}
                  onChange={() => setTeamExpenseMode('total')}
                  className="w-5 h-5 text-orange-600 focus:ring-orange-500"
                />
                <span className="ml-2 font-medium text-gray-700">Valor Total (Rateio Automático)</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="teamMode"
                  checked={teamExpenseMode === 'unit'}
                  onChange={() => setTeamExpenseMode('unit')}
                  className="w-5 h-5 text-orange-600 focus:ring-orange-500"
                />
                <span className="ml-2 font-medium text-gray-700">Valor por Pessoa</span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Coluna Esquerda: Dados Básicos */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data da Despesa *</label>
                  <input
                    type="date"
                    name="expense_date"
                    value={formData.expense_date}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Selecione...</option>
                    {categories
                      .filter(c => c.cost_scope === 'EQUIPE' || c.cost_scope === 'TODAS' || (!c.cost_scope && ['aliment', 'refeiç', 'transporte', 'vale', 'happy hour', 'viagem', 'diária', 'equipe'].some(k => c.name.toLowerCase().includes(k))))
                      .map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor (Opcional)</label>
                  <select
                    name="supplier_id"
                    value={formData.supplier_id}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Selecione...</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento (Opcional)</label>
                  <select
                    name="payment_method"
                    value={formData.payment_method}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Selecione...</option>
                    <option value="dinheiro">Dinheiro</option>
                    <option value="pix">PIX</option>
                    <option value="cartao_debito">Cartão de Débito</option>
                    <option value="cartao_credito">Cartão de Crédito</option>
                    <option value="transferencia">Transferência Bancária</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nº Recibo/Nota (Opcional)</label>
                  <input
                    type="text"
                    name="receipt_number"
                    value={formData.receipt_number}
                    onChange={handleInputChange}
                    placeholder="Ex: 12345"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Veículo (Opcional)</label>
                  <input
                    type="text"
                    name="vehicle"
                    value={formData.vehicle}
                    onChange={handleInputChange}
                    placeholder="Ex: Carro da empresa"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quilometragem (km) (Opcional)</label>
                  <input
                    type="number"
                    name="km_odometer"
                    value={formData.km_odometer}
                    onChange={handleInputChange}
                    placeholder="Ex: 50000"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    name="observations"
                    value={formData.observations}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="Detalhes adicionais..."
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Ex: Lanche da Tarde"
                    required
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {teamExpenseMode === 'total' ? 'Valor TOTAL (R$) *' : 'Valor por PESSOA (R$) *'}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">R$</span>
                    <input
                      type="number"
                      name="base_price_per_unit"
                      value={formData.base_price_per_unit}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                      className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 font-bold text-lg"
                    />
                  </div>
                  {teamExpenseMode === 'total' && formData.base_price_per_unit && formData.selected_employees?.length > 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      Será R$ {(parseFloat(formData.base_price_per_unit) / formData.selected_employees.length).toFixed(2)} para cada um dos {formData.selected_employees.length} funcionários.
                    </p>
                  )}
                  {teamExpenseMode === 'unit' && formData.base_price_per_unit && formData.selected_employees?.length > 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      Total da Nota: R$ {(parseFloat(formData.base_price_per_unit) * formData.selected_employees.length).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>

              {/* Coluna Direita: Seleção de Funcionários */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Funcionários ({formData.selected_employees?.length || 0}) *
                </label>
                <div className="border border-gray-300 rounded-lg max-h-[300px] overflow-y-auto bg-white p-2">
                  <div className="grid grid-cols-1 gap-2">
                    {employees.map(employee => {
                      const isSelected = formData.selected_employees?.includes(employee.id);
                      return (
                        <div
                          key={employee.id}
                          onClick={() => handleEmployeeSelection(employee.id)}
                          className={`p-2 rounded cursor-pointer flex items-center gap-3 transition-colors ${isSelected ? 'bg-orange-100 border border-orange-200' : 'hover:bg-gray-50 border border-transparent'
                            }`}
                        >
                          <div className={`w-5 h-5 border rounded flex items-center justify-center ${isSelected ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                            }`}>
                            {isSelected && <span className="text-white text-xs">✓</span>}
                          </div>
                          <span className="text-gray-700 font-medium">{employee.full_name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      const allIds = employees.map(e => e.id);
                      const currentIds = formData.selected_employees || [];
                      if (currentIds.length === allIds.length) {
                        // Desmarcar todos
                        setFormData(prev => ({ ...prev, selected_employees: [] }));
                      } else {
                        // Marcar todos
                        setFormData(prev => ({ ...prev, selected_employees: allIds }));
                      }
                    }}
                    className="text-sm text-orange-600 hover:text-orange-800 font-medium"
                  >
                    {formData.selected_employees?.length === employees.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                  </button>
                </div>
              </div>
            </div>

            {/* Rodapé do Formulário */}
            <div className="pt-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowForm(false); resetForm(); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 shadow-sm flex items-center gap-2"
                disabled={loading}
              >
                {loading ? 'Processando...' : '💾 Lançar Despesas'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Modal de Variações */}
      {showVariations && selectedCostForVariations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">
                🎨 Variações de Preço - {selectedCostForVariations.description}
              </h2>
              <button
                onClick={handleCloseVariations}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <CostVariationsManager
                costId={selectedCostForVariations.id}
                costData={selectedCostForVariations}
                onVariationsChange={loadCosts}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de Preços Dinâmicos */}
      {showDynamicPricing && selectedCostForPricing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">
                💰 Preços Dinâmicos - {selectedCostForPricing.description}
              </h2>
              <button
                onClick={handleCloseDynamicPricing}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <DynamicPricingManager
                costId={selectedCostForPricing.id}
                costData={selectedCostForPricing}
                onPricingChange={loadCosts}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de Ver Abates */}
      {showAbatesModal && selectedCostAbates && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-purple-50">
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  📋 Abates da Despesa
                </h2>
                <p className="text-sm text-gray-600">
                  {selectedCostAbates.description} - Fornecedor: {selectedCostAbates.supplier_name || 'N/A'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAbatesModal(false);
                  setSelectedCostAbates(null);
                  setCostAbatesList([]);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Resumo da despesa */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Valor Total</p>
                  <p className="text-lg font-bold text-gray-800">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedCostAbates.base_price_per_unit || 0)}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Já Abatido</p>
                  <p className="text-lg font-bold text-green-600">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedCostAbates.total_abated || 0)}
                  </p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Restante</p>
                  <p className="text-lg font-bold text-yellow-600">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedCostAbates.remaining_amount || 0)}
                  </p>
                </div>
              </div>

              {/* Botão para quitar pela empresa */}
              {parseFloat(selectedCostAbates.remaining_amount) > 0 && (
                <div className="mb-4">
                  <button
                    onClick={() => {
                      setShowAbatesModal(false);
                      setShowPayByCompanyModal(true);
                      setPayByCompanyAmount(selectedCostAbates.remaining_amount);
                    }}
                    className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 shadow-sm flex items-center gap-2"
                  >
                    💰 Quitar pela Empresa (R$ {parseFloat(selectedCostAbates.remaining_amount).toFixed(2)})
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    Clique para registrar o pagamento do valor restante pela empresa
                  </p>
                </div>
              )}

              {/* Lista de abates */}
              <h3 className="text-lg font-semibold text-gray-700 mb-3">Abates Registrados</h3>
              {costAbatesLoading ? (
                <div className="text-center py-8 text-gray-500">Carregando...</div>
              ) : costAbatesList.length === 0 ? (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                  Nenhum abate registrado para esta despesa.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-600">Projeto</th>
                        <th className="px-3 py-2 text-left text-gray-600">Status</th>
                        <th className="px-3 py-2 text-right text-gray-600">Valor</th>
                        <th className="px-3 py-2 text-center text-gray-600">Data Pagamento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costAbatesList.map((abate) => (
                        <tr key={abate.id} className="border-t">
                          <td className="px-3 py-2">
                            <div>
                              <p className="font-medium">{abate.project_code || '-'}</p>
                              <p className="text-xs text-gray-500">{abate.client_name || '-'}</p>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              abate.status === 'PAID' ? 'bg-green-100 text-green-800' :
                              abate.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {abate.status === 'PAID' ? 'Pago' :
                               abate.status === 'PENDING' ? 'Pendente' : 'Cancelado'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(abate.amount || 0)}
                          </td>
                          <td className="px-3 py-2 text-center text-gray-500">
                            {abate.payment_date || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal para Quitar pela Empresa */}
      {showPayByCompanyModal && selectedCostAbates && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-red-50">
              <h2 className="text-xl font-bold text-gray-800">
                💰 Quitar pela Empresa
              </h2>
              <button
                onClick={() => {
                  setShowPayByCompanyModal(false);
                  setPayByCompanyAmount('');
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Esta ação registrará o pagamento do valor restante da despesa personalizada pela empresa.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-500">Despesa</p>
                <p className="font-medium">{selectedCostAbates.description}</p>
                <p className="text-sm text-gray-500 mt-2">Valor Restante</p>
                <p className="text-xl font-bold text-red-600">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedCostAbates.remaining_amount || 0)}
                </p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor a pagar *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={selectedCostAbates.remaining_amount}
                  value={payByCompanyAmount}
                  onChange={(e) => setPayByCompanyAmount(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowPayByCompanyModal(false);
                    setPayByCompanyAmount('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    if (!payByCompanyAmount || parseFloat(payByCompanyAmount) <= 0) {
                      toast.error('Informe um valor válido');
                      return;
                    }
                    if (parseFloat(payByCompanyAmount) > parseFloat(selectedCostAbates.remaining_amount)) {
                      toast.error('Valor não pode exceder o restante');
                      return;
                    }
                    try {
                      const result = await costService.payCostByCompany(selectedCostAbates.id, {
                        amount: payByCompanyAmount,
                        payment_date: new Date().toISOString().split('T')[0]
                      });
                      toast.success(result.message || 'Pagamento registrado com sucesso!');
                      setShowPayByCompanyModal(false);
                      setPayByCompanyAmount('');
                      loadCustomExpenses();
                      // Invalidar queries de saldos iniciais para atualizar status
                      queryClient.invalidateQueries(['projectInitialBalances']);
                      queryClient.invalidateQueries(['projectInitialBalancesEntries']);
                    } catch (error) {
                      toast.error(error?.response?.data?.message || 'Erro ao quitar despesa');
                    }
                  }}
                  className="px-6 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 shadow-sm"
                >
                  Confirmar Pagamento
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pesos de Preferências de Material */}
      {showMaterialWeights && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">
                ⚖️ Pesos de Preferências de Material
              </h2>
              <button
                onClick={() => setShowMaterialWeights(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <MaterialPricingWeightsManager
                onClose={() => setShowMaterialWeights(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de Upload de PDF de Cortes */}
      {showCortesPdfUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <CortesPdfUpload
            onClose={() => setShowCortesPdfUpload(false)}
            onSuccess={() => {
              setShowCortesPdfUpload(false);
              // Recarregar lista se necessário
            }}
          />
        </div>
      )}

      {/* Modal de Lista de PDFs Processados */}
      {showCortesPdfList && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full my-8">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">
                📋 PDFs de Cortes Processados
              </h2>
              <button
                onClick={() => setShowCortesPdfList(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <CortesPdfList />
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmParcel.show}
        onConfirm={handleConfirmParcelAction}
        onCancel={() => setConfirmParcel({ show: false, parcelId: null })}
        title="Confirmar Pagamento"
        message="Confirmar o pagamento desta parcela? O valor será debitado do Caixa da Empresa."
        confirmText="Confirmar"
        type="success"
      />

      {/* Modal de Subpedidos da Despesa Personalizada */}
      {showSubOrdersModal && selectedCostForSubOrders && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-purple-50 to-white">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xl">📦</span>
                  <h2 className="text-xl font-bold text-gray-900">
                    Subpedidos e Incrementos do Pedido
                  </h2>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  <strong>{selectedCostForSubOrders.description}</strong> • Fornecedor: {selectedCostForSubOrders.supplier_name || 'N/A'} • Total Acumulado: <strong className="text-purple-700">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedCostForSubOrders.base_price_per_unit || 0)}</strong>
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedCostForSubOrders.order_status !== 'CLOSED' && (
                  <button
                    onClick={() => handleOpenNewSubOrder(selectedCostForSubOrders)}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-sm flex items-center gap-1"
                  >
                    <span>➕ Novo Subpedido</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowSubOrdersModal(false);
                    setSelectedCostForSubOrders(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl px-2 leading-none"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {loadingSubOrders ? (
                <div className="text-center py-12 text-gray-500">Carregando subpedidos...</div>
              ) : subOrdersData.sub_orders.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                  <p className="text-gray-600 font-medium">Nenhum subpedido registrado para esta despesa.</p>
                  <p className="text-xs text-gray-400 mt-1">Clique em "+ Novo Subpedido" para adicionar o primeiro lote de materiais.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {subOrdersData.sub_orders.map((subOrder) => (
                    <div
                      key={subOrder.id}
                      className="bg-white border rounded-xl shadow-sm overflow-hidden border-gray-200"
                    >
                      <div className="p-4 bg-gray-50/70 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xs">
                            #{subOrder.id}
                          </span>
                          <div>
                            <h4 className="font-bold text-gray-900 text-sm">
                              {subOrder.sub_order_number}
                            </h4>
                            <p className="text-xs text-gray-500">
                              {subOrder.description || 'Lote de materiais'} • Criado em: {subOrder.sub_order_date ? formatLocalDateBR(subOrder.sub_order_date) : '-'}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <span className="text-[11px] text-gray-500 block">Total do Subpedido</span>
                            <span className="text-sm font-bold text-gray-900">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(subOrder.total_amount || 0)}
                            </span>
                          </div>

                          <div>
                            {subOrder.status === 'CONFIRMED' ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                                ✓ No Estoque ({subOrder.warehouse_name || 'Armazém'})
                              </span>
                            ) : (
                              <button
                                onClick={() => handleOpenConfirmStock(selectedCostForSubOrders.id, subOrder)}
                                className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-medium shadow-sm flex items-center gap-1"
                              >
                                <span>📥 Dar Entrada no Estoque</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Tabela de Itens deste Subpedido */}
                      <div className="p-3 overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100/60 text-gray-600 font-semibold">
                            <tr>
                              <th className="px-3 py-2 text-left">Produto / Variante</th>
                              <th className="px-3 py-2 text-center">Cor / Detalhes</th>
                              <th className="px-3 py-2 text-center">Qtd</th>
                              <th className="px-3 py-2 text-right">Preço Unit.</th>
                              <th className="px-3 py-2 text-right">Subtotal</th>
                              <th className="px-3 py-2 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {subOrder.items && subOrder.items.length > 0 ? (
                              subOrder.items.map((it) => (
                                <tr key={it.id} className="hover:bg-gray-50/40">
                                  <td className="px-3 py-2 font-medium text-gray-900">
                                    {it.product_name || it.variant_name || `Item #${it.id}`}
                                  </td>
                                  <td className="px-3 py-2 text-center text-gray-500">
                                    {it.cor || it.espessura ? `${it.cor || ''} ${it.espessura ? it.espessura + 'mm' : ''}` : '-'}
                                  </td>
                                  <td className="px-3 py-2 text-center font-bold text-gray-800">
                                    {it.quantity}
                                  </td>
                                  <td className="px-3 py-2 text-right text-gray-600">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(it.unit_price || 0)}
                                  </td>
                                  <td className="px-3 py-2 text-right font-bold text-gray-900">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(it.total_price || (it.quantity * it.unit_price) || 0)}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                      it.status === 'CONFIRMED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {it.status === 'CONFIRMED' ? 'No estoque' : 'Pendente'}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={6} className="px-3 py-3 text-center text-gray-400">
                                  Nenhum item vinculado diretamente a este subpedido.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={() => {
                  setShowSubOrdersModal(false);
                  setSelectedCostForSubOrders(null);
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-sm font-medium"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Novo Subpedido / Incremento */}
      {showNewSubOrderModal && selectedCostForSubOrders && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-emerald-50">
              <div>
                <h3 className="text-lg font-bold text-emerald-950 flex items-center gap-2">
                  <span>➕ Adicionar Novo Subpedido (Incremento de Itens)</span>
                </h3>
                <p className="text-xs text-emerald-700 mt-0.5">
                  Pedido: <strong>{selectedCostForSubOrders.description}</strong> • Fornecedor: {selectedCostForSubOrders.supplier_name || 'N/A'}
                </p>
              </div>
              <button
                onClick={() => setShowNewSubOrderModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSaveNewSubOrder} className="p-6 overflow-y-auto flex-1 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Identificação do Subpedido
                  </label>
                  <input
                    type="text"
                    value={newSubOrderFormData.sub_order_number}
                    onChange={(e) => setNewSubOrderFormData(prev => ({ ...prev, sub_order_number: e.target.value }))}
                    placeholder="Ex: Subpedido #2, Lote de Dobradiças..."
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                  />
                  <span className="text-[11px] text-gray-400">Deixe em branco para numeração automática</span>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Data do Subpedido *
                  </label>
                  <input
                    type="date"
                    required
                    value={newSubOrderFormData.sub_order_date}
                    onChange={(e) => setNewSubOrderFormData(prev => ({ ...prev, sub_order_date: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Valor Deste Subpedido
                  </label>
                  <div className="w-full p-2 border border-emerald-200 rounded-lg bg-emerald-50 text-emerald-800 font-bold text-sm">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(newSubOrderValidation.totalAmount || 0)}
                  </div>
                </div>

                <div className="md:col-span-3">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Descrição / Finalidade da Remessa
                  </label>
                  <input
                    type="text"
                    value={newSubOrderFormData.description}
                    onChange={(e) => setNewSubOrderFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Ex: Reforço de chapas amadeiradas para closet..."
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div className="pt-2">
                <label className="block text-xs font-bold text-gray-800 mb-2 uppercase tracking-wide">
                  Itens a Incrementar Neste Subpedido
                </label>
                <PurchaseItemsGrid
                  items={newSubOrderItems}
                  setItems={setNewSubOrderItems}
                  supplierId={selectedCostForSubOrders?.supplier_id}
                  onValidationChange={(isValid, itemCount, totalAmount) => {
                    setNewSubOrderValidation({ isValid, itemCount, totalAmount });
                  }}
                  errors={newSubOrderItems.length === 0 ? { items: 'Adicione pelo menos um item ao subpedido' } : {}}
                />
              </div>

              <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Ao salvar, o valor deste subpedido será somado automaticamente ao total do pedido principal.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowNewSubOrderModal(false)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={newSubOrderItems.length === 0}
                    className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold shadow-sm disabled:opacity-50"
                  >
                    Salvar e Incrementar Pedido
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Entrada no Estoque */}
      {showConfirmStockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-purple-50 flex justify-between items-center">
              <h3 className="text-base font-bold text-purple-950">
                📥 Confirmar Entrada no Estoque
              </h3>
              <button
                onClick={() => setShowConfirmStockModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleExecuteConfirmStock} className="p-6 space-y-4">
              <p className="text-xs text-gray-600">
                Você está dando entrada no estoque para os materiais de: <strong className="text-gray-900">{confirmStockData.subOrderNumber}</strong>.
              </p>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Armazém de Destino *
                </label>
                <select
                  required
                  value={confirmStockData.warehouse_id}
                  onChange={(e) => setConfirmStockData(prev => ({ ...prev, warehouse_id: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Selecione o armazém...</option>
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id}>{w.nome || w.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Observações da Entrada
                </label>
                <textarea
                  rows={2}
                  value={confirmStockData.observations}
                  onChange={(e) => setConfirmStockData(prev => ({ ...prev, observations: e.target.value }))}
                  placeholder="Ex: Recebido conforme nota fiscal, inspecionado por..."
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowConfirmStockModal(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={confirmingStockLoading || !confirmStockData.warehouse_id}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-semibold shadow-sm disabled:opacity-50"
                >
                  {confirmingStockLoading ? 'Confirmando...' : 'Confirmar Entrada Física'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Pagamento de Parcela com Forma de Pagamento */}
      {confirmParcel.show && confirmParcel.parcel && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="px-6 py-4 bg-gradient-to-r from-emerald-600 to-green-700 text-white flex justify-between items-center">
              <div>
                <h3 className="text-base font-bold flex items-center gap-2">
                  <span>💵 Confirmar Pagamento de Parcela</span>
                </h3>
                <p className="text-xs text-emerald-100 mt-0.5">
                  Lançar saída no Caixa da Empresa
                </p>
              </div>
              <button
                type="button"
                onClick={() => setConfirmParcel({ show: false, parcel: null, payment_method: 'pix' })}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-3 border border-gray-200 text-xs space-y-1">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-500 uppercase">Despesa:</span>
                  <span className="font-bold text-gray-900">{editingCost?.description || confirmParcel.parcel.description}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-500 uppercase">Vencimento:</span>
                  <span className="font-medium text-gray-800">
                    {confirmParcel.parcel.due_date ? formatLocalDateBR(confirmParcel.parcel.due_date) : '-'}
                  </span>
                </div>
                <div className="pt-1.5 border-t border-gray-200 flex justify-between items-center">
                  <span className="font-semibold text-gray-700">Valor a Pagar:</span>
                  <span className="text-base font-extrabold text-emerald-700">
                    {formatCurrency(confirmParcel.parcel.amount)}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  Forma de Pagamento Desta Parcela *
                </label>
                <select
                  value={confirmParcel.payment_method}
                  onChange={(e) => setConfirmParcel(prev => ({ ...prev, payment_method: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm font-semibold bg-white text-gray-800 focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="pix">⚡ PIX</option>
                  <option value="dinheiro">💵 Dinheiro em Espécie</option>
                  <option value="cartao_debito">💳 Cartão de Débito</option>
                  <option value="cartao_credito">💳 Cartão de Crédito</option>
                  <option value="transferencia">🏦 Transferência Bancária</option>
                  <option value="cheque">📝 Cheque</option>
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  Você pode confirmar usando a forma original ou selecionar outro método específico para esta parcela.
                </p>
              </div>

              <div className="flex gap-3 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setConfirmParcel({ show: false, parcel: null, payment_method: 'pix' })}
                  className="flex-1 px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmParcelAction}
                  disabled={confirmParcelMutation.isPending}
                  className="flex-1 px-4 py-2 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-xl shadow-xs transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  <Check className="w-4 h-4" />
                  {confirmParcelMutation.isPending ? 'Confirmando...' : 'Confirmar e Pagar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmDelete.show}
        onConfirm={handleConfirmDeleteAction}
        onCancel={() => setConfirmDelete({ show: false, costId: null })}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja remover este custo?"
        confirmText="Remover"
        type="danger"
      />

      <ConfirmModal
        show={confirmBulkDelete.show}
        onConfirm={handleConfirmBulkDeleteAction}
        onCancel={() => setConfirmBulkDelete({ show: false })}
        title="Confirmar Exclusão em Massa"
        message={`Tem certeza que deseja remover ${selectedCosts.length} custo(s)?`}
        confirmText="Remover Todos"
        type="danger"
      />

      <ConfirmModal
        show={confirmDeleteCustomCost.show}
        onConfirm={handleConfirmDeleteCustomCostAction}
        onCancel={() => setConfirmDeleteCustomCost({ show: false, cost: null })}
        title="Excluir Despesa Personalizada"
        message={`Tem certeza que deseja excluir a despesa personalizada "${confirmDeleteCustomCost.cost?.description}" (ID #${confirmDeleteCustomCost.cost?.id})? Como ela ainda não possui valores abatidos, será removida com segurança.`}
        confirmText="Excluir"
        type="danger"
      />
    </div>
  );
};

export default CostsManagementEnhanced;
